import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/BattleSimulation.css";
import { PageShell, SectionCard } from "../components/ui";
import axiosInstance from "../utils/axiosInstance";
import { API_URL } from "../utils/runtimeConfig";
import { AuthContext } from "../context/AuthContext";
import { buildBattleLogReportView } from "../components/events/battleLogReportAdapter";
import ArmySetupPanel from "./battle-simulation/ArmySetupPanel";
import BattlefieldFormation from "./battle-simulation/BattlefieldFormation";
import BattleReportSection from "./battle-simulation/BattleReportSection";
import CommanderCard from "./battle-simulation/CommanderCard";
import CommanderSelectionModal from "./battle-simulation/CommanderSelectionModal";
import {
  DEFAULT_BATTLE_SIM_CONFIG,
  createArmyState,
} from "./battle-simulation/battleSimulationConfig";
import {
  DEFAULT_BATTLE_PROJECTION_MODE,
  projectFrame,
} from "./battle-simulation/battleProjection";
import {
  createCommanderProfile,
  getReportBaseline,
  listCombatProfiles,
  listCommanderProfiles,
  listReportBaselines,
} from "./battle-simulation/profileApi";
import {
  buildParityCommanderFromProfile,
  DEFAULT_COMMANDER_PARITY_PROFILE,
} from "./battle-simulation/commanderParityCompiler";
import {
  OFFICER_ACTIVE_SKILL_OPTIONS,
  OFFICER_BASE_ROWS,
  OFFICER_PASSIVE_SKILL_OPTIONS,
  getOfficerDisplayIconPath,
} from "./battle-simulation/data/officerCatalog";

const ROW_ATTACK_RANGE = { front: 0, mid: 1, back: 2, special: 2 };
const ROW_DAMAGE_FACTOR = { front: 0.055, mid: 0.045, back: 0.038, special: 0.03 };

const DEFAULT_TRACE_FPS = 50;
const SERVER_PARITY_MAX_TRACE_FRAMES = 20000;
const SERVER_PARITY_MAX_RUNTIME_MS = 30000;
const TRACE_LINE_PERSIST_SECONDS = 0.5;
const TRACE_LINE_MIN_OPACITY = 0.18;

const BATTLE_TYPE_OPTIONS = [
  { value: "nation_siege", label: "Nation Siege" },
  { value: "uranium_mine", label: "Uranium Mine" },
];

const PARITY_ENGINE_VERSION = "lua_frame_v1";
const TITAN_WTYPE_EMPRESS = 20;
const TITAN_WTYPE_BISON = 21;

const formatBattleClock = (secondsValue) => {
  const totalSeconds = Math.max(0, Math.floor(Number(secondsValue || 0)));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const parseReportIdFromIdentityKey = (identityKey) => {
  const raw = String(identityKey || "").trim();
  if (!raw) return "";
  const match = raw.match(/(?:^|\|)report:([^|]+)/i);
  return match ? String(match[1] || "").trim() : "";
};

const DEFAULT_COMMANDER_POWER_SUMMARY = {
  battlePower: 0,
  officerPower: 0,
  titanPower: 0,
  warplanePower: 0,
  antiquesPower: 0,
  equipmentPower: 0,
};

const DEFAULT_TIER_STATS = {
  hp: 2000,
  defense: 100,
  attack: 150,
  attackCooldown: 2,
  attackRange: 1,
  units: 1,
};

const PREVIEW_TECH_GIDS = Object.freeze({
  ATTACK_ROW: [50397213, 50397215, 50397217],
  HP_ROW: [50397214, 50397216, 50397218],
  DEFENSE_ROW: [50397275, 50397276, 50397277],
  DAMAGE_TO_ROW: [50397247, 50397246, 50397245],
  DAMAGE_FROM_ROW: [50397337, 50397336, 50397335],
  ATTACK_TO_ROW: [50397422, 50397423, 50397424],
  ATTACK_TO_BIO: 50397425,
  SUB_ATTACK_TO_ROW: [50397426, 50397427, 50397428],
  SUB_ATTACK_TO_BIO: 50397429,
  SUB_ATTACK_TO_ALL: 50397430,
  SUB_DEFENSE_TO_ROW: [50397436, 50397437, 50397438],
  BIOTECH_ATTACK_RATE: 50397353,
  BIOTECH_HP_RATE: 50397354,
  BIOTECH_DEFENSE_RATE: 50397355,
  BLOCK_RATE_ALL: 50397507,
  BLOCK_ALL: 50462930,
  BAN_SKILL_RESIST_ALL: 50462937,
  MISFIRE_RESIST_ALL: 50462940,
  MISC_ATTACK_AMOUNT: 50462916,
  MISC_HP_AMOUNT: 50462920,
  DAMAGE_TO_TITAN: 50397459,
  DAMAGE_FROM_TITAN: 50397460,
});

const PREVIEW_BONUS_METRICS = Object.freeze([
  {
    key: "meleeHp",
    label: "Melee HP",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.HP_ROW[0]) || 0) / 10,
  },
  {
    key: "midRangeHp",
    label: "Mid-Range HP",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.HP_ROW[1]) || 0) / 10,
  },
  {
    key: "longRangeHp",
    label: "Long-Range HP",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.HP_ROW[2]) || 0) / 10,
  },
  {
    key: "meleeAttack",
    label: "Melee Attack",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.ATTACK_ROW[0]) || 0) / 10,
  },
  {
    key: "midRangeAttack",
    label: "Mid-Range Attack",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.ATTACK_ROW[1]) || 0) / 10,
  },
  {
    key: "longRangeAttack",
    label: "Long-Range Attack",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.ATTACK_ROW[2]) || 0) / 10,
  },
  {
    key: "meleeDefense",
    label: "Melee Defense",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DEFENSE_ROW[0]) || 0) / 10,
  },
  {
    key: "midRangeDefense",
    label: "Mid-Range Defense",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DEFENSE_ROW[1]) || 0) / 10,
  },
  {
    key: "longRangeDefense",
    label: "Long-Range Defense",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DEFENSE_ROW[2]) || 0) / 10,
  },
  {
    key: "increaseDamageToMeleeTroops",
    label: "Increases Damage to Melee Troops",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_TO_ROW[0]) || 0) / 10,
  },
  {
    key: "increaseDamageToMidRangeTroops",
    label: "Increases Damage to Mid-Range Troops",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_TO_ROW[1]) || 0) / 10,
  },
  {
    key: "increaseDamageToLongRangeTroops",
    label: "Increases Damage to Long-Range Troops",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_TO_ROW[2]) || 0) / 10,
  },
  {
    key: "reducesDamageTakenFromMeleeTroops",
    label: "Reduces Damage taken from Melee Troops",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_FROM_ROW[0]) || 0) / 10,
  },
  {
    key: "reducesDamageTakenFromMidRangeTroops",
    label: "Reduces Damage taken from Mid-Range Troops",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_FROM_ROW[1]) || 0) / 10,
  },
  {
    key: "reducesDamageTakenFromLongRangeTroops",
    label: "Reduces Damage taken from Long-Range Troops",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_FROM_ROW[2]) || 0) / 10,
  },
  {
    key: "biochemicalZombiesAttackBoost",
    label: "Biochemical Zombies Attack Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.BIOTECH_ATTACK_RATE) || 0) / 10,
  },
  {
    key: "biochemicalZombiesHpBoost",
    label: "Biochemical Zombies HP Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.BIOTECH_HP_RATE) || 0) / 10,
  },
  {
    key: "biochemicalZombiesDefenseBoost",
    label: "Biochemical Zombies Defense Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.BIOTECH_DEFENSE_RATE) || 0) / 10,
  },
  {
    key: "allTroopsBlock",
    label: "All Troops Block",
    format: "raw_plus_percent",
    compute: (techMap) => Number(techMap.get(PREVIEW_TECH_GIDS.BLOCK_ALL) || 0)
      + (Number(techMap.get(PREVIEW_TECH_GIDS.BLOCK_RATE_ALL) || 0) / 1000),
    computePercent: (techMap) => {
      const value = Number(techMap.get(PREVIEW_TECH_GIDS.BLOCK_ALL) || 0)
        + (Number(techMap.get(PREVIEW_TECH_GIDS.BLOCK_RATE_ALL) || 0) / 1000);
      return rateValueToPercent(value);
    },
  },
  {
    key: "allTroopsMisfireResistance",
    label: "All Troops Misfire Resistance",
    format: "raw_plus_percent",
    compute: (techMap) => Number(techMap.get(PREVIEW_TECH_GIDS.MISFIRE_RESIST_ALL) || 0),
    computePercent: (techMap) => rateValueToPercent(
      Number(techMap.get(PREVIEW_TECH_GIDS.MISFIRE_RESIST_ALL) || 0),
    ),
  },
  {
    key: "allTroopsDisruptorResistance",
    label: "All Troops Disruptor Resistance",
    format: "raw_plus_percent",
    compute: (techMap) => Number(techMap.get(PREVIEW_TECH_GIDS.BAN_SKILL_RESIST_ALL) || 0),
    computePercent: (techMap) => rateValueToPercent(
      Number(techMap.get(PREVIEW_TECH_GIDS.BAN_SKILL_RESIST_ALL) || 0),
    ),
  },
  {
    key: "attackAgainstMeleeBoost",
    label: "Attack Against Melee Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.ATTACK_TO_ROW[0]) || 0) / 10,
  },
  {
    key: "attackAgainstMidRangeBoost",
    label: "Attack Against Mid-Range Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.ATTACK_TO_ROW[1]) || 0) / 10,
  },
  {
    key: "attackAgainstLongRangeBoost",
    label: "Attack Against Long-Range Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.ATTACK_TO_ROW[2]) || 0) / 10,
  },
  {
    key: "attackAgainstBiochemicalZombieBoost",
    label: "Attack Against Biochemical Zombies Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.ATTACK_TO_BIO) || 0) / 10,
  },
  {
    key: "enemyMeleeAttackReduction",
    label: "Enemy Melee Attack Reduction",
    format: "percent",
    compute: (techMap) => ((techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_ROW[0]) || 0)
      + (techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_ALL) || 0)) / 10,
  },
  {
    key: "enemyMidRangeAttackReduction",
    label: "Enemy Mid-Range Attack Reduction",
    format: "percent",
    compute: (techMap) => ((techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_ROW[1]) || 0)
      + (techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_ALL) || 0)) / 10,
  },
  {
    key: "enemyLongRangeAttackReduction",
    label: "Enemy Long-Range Attack Reduction",
    format: "percent",
    compute: (techMap) => ((techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_ROW[2]) || 0)
      + (techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_ALL) || 0)) / 10,
  },
  {
    key: "enemyBiochemicalZombieAttackReduction",
    label: "Enemy Biochemical Zombie Attack Reduction",
    format: "percent",
    compute: (techMap) => ((techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_BIO) || 0)
      + (techMap.get(PREVIEW_TECH_GIDS.SUB_ATTACK_TO_ALL) || 0)) / 10,
  },
  {
    key: "enemyMeleeDefenseReduction",
    label: "Enemy Melee Defense Reduction",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.SUB_DEFENSE_TO_ROW[0]) || 0) / 10,
  },
  {
    key: "troopsAttackBonusBoost",
    label: "Troops Attack Bonus Boost",
    format: "raw",
    compute: (techMap) => Number(techMap.get(PREVIEW_TECH_GIDS.MISC_ATTACK_AMOUNT) || 0),
  },
  {
    key: "troopsHpBonusBoost",
    label: "Troops HP Bonus Boost",
    format: "raw",
    compute: (techMap) => Number(techMap.get(PREVIEW_TECH_GIDS.MISC_HP_AMOUNT) || 0),
  },
  {
    key: "damageAgainstTitansBoost",
    label: "Damage Against Titans Boost",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_TO_TITAN) || 0) / 10,
  },
  {
    key: "titanDamageTakenReduction",
    label: "Titan Damage Taken Reduction",
    format: "percent",
    compute: (techMap) => (techMap.get(PREVIEW_TECH_GIDS.DAMAGE_FROM_TITAN) || 0) / 10,
  },
]);

const parseArmyValue = (raw) => {
  const parsed = Number(String(raw || "").replace(/[^\d]/g, ""));
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed);
};

const toNonNegativeInt = (value, fallback = 0) => {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};

const clonePlainObject = (value) => {
  if (!value || typeof value !== "object") return {};
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return Array.isArray(value) ? [...value] : { ...value };
  }
};

const formatCompactDateTime = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "Unknown time";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatUnits = (value) => toNonNegativeInt(value, 0).toLocaleString();

const summarizeReportRows = (rows = []) => {
  const normalizedRows = Array.isArray(rows) ? rows : [];
  return normalizedRows.reduce((acc, row) => {
    const survivingUnits = toNonNegativeInt(row?.survivingUnits, 0);
    const lostUnits = toNonNegativeInt(row?.lostUnits, 0);
    acc.survivingUnits += survivingUnits;
    acc.lostUnits += lostUnits;
    acc.woundedUnits += toNonNegativeInt(row?.woundedUnits, 0);
    acc.enemyUnitsKilled += toNonNegativeInt(row?.enemyUnitsKilled, 0);
    acc.totalUnits += survivingUnits + lostUnits;
    return acc;
  }, {
    totalUnits: 0,
    survivingUnits: 0,
    woundedUnits: 0,
    lostUnits: 0,
    enemyUnitsKilled: 0,
    battlers: normalizedRows.length ? 1 : 0,
  });
};

const getReportUserView = (baseline = null, side = "attacker") => {
  const report = baseline?.reportPayload && typeof baseline.reportPayload === "object"
    ? baseline.reportPayload
    : {};
  const commanderId = toNonNegativeInt(side === "defender" ? report?.def : report?.att, 0);
  const users = report?.users && typeof report.users === "object" ? report.users : {};
  const directUser = users[String(commanderId)] && typeof users[String(commanderId)] === "object"
    ? users[String(commanderId)]
    : null;
  if (directUser) return directUser;
  const marchingTroops = Array.isArray(report?.marchingTroops) ? report.marchingTroops : [];
  const troop = marchingTroops.find((entry) => (
    toNonNegativeInt(entry?.uid, 0) === commanderId
    || toNonNegativeInt(entry?.u_v?.id, 0) === commanderId
  ));
  return troop?.u_v && typeof troop.u_v === "object" ? troop.u_v : {};
};

const buildBaselineCommanderProfile = (baseline = null, side = "attacker") => {
  const report = baseline?.reportPayload && typeof baseline.reportPayload === "object"
    ? baseline.reportPayload
    : {};
  const userView = getReportUserView(baseline, side);
  const commanderId = toNonNegativeInt(side === "defender" ? report?.def : report?.att, 0);
  const name = String(userView?.nick || userView?.name || "").trim() || `${side === "defender" ? "Defender" : "Attacker"} ${commanderId || ""}`.trim();
  return {
    name,
    linkedCommanderName: name,
    linkedGameCommanderId: commanderId || null,
    linkedCommanderGameId: commanderId || null,
    allianceShort: String(userView?.asn || "").trim(),
    cityLevel: toNonNegativeInt(userView?.ct_lvl, 0) || null,
    parityProfile: baseline?.battleInput?.commanderProfiles?.[side] || {},
  };
};

const getAction20019PayloadFromBaseline = (baseline = null) => {
  const candidates = [
    baseline?.reportPayload,
    baseline?.battleInput,
    baseline?.report,
    baseline?.payload?.reportPayload,
    baseline?.payload?.battleInput,
  ];
  return candidates.find((candidate) => (
    candidate
    && typeof candidate === "object"
    && Array.isArray(candidate.marchingTroops)
    && candidate.marchingTroops.length > 0
  )) || null;
};

const getProfileGameCommanderId = (profile = null) => toNonNegativeInt(
  profile?.linkedGameCommanderId
    ?? profile?.linkedCommanderGameId
    ?? profile?.gameCommanderId
    ?? profile?.commanderGameId,
  0,
);

const getCaptureTroopCommanderId = (troop = null) => toNonNegativeInt(
  troop?.uid ?? troop?.u_v?.id ?? troop?.id,
  0,
);

const getCaptureTroopAllianceId = (troop = null) => toNonNegativeInt(
  troop?.asid ?? troop?.u_v?.asid,
  0,
);

const getCaptureTroopForProfile = (payload = null, profile = null, targetSide = "attacker") => {
  const report = payload && typeof payload === "object" ? payload : {};
  const marchingTroops = Array.isArray(report.marchingTroops) ? report.marchingTroops : [];
  if (!marchingTroops.length) return null;
  const profileCommanderId = getProfileGameCommanderId(profile);
  if (profileCommanderId > 0) {
    const matched = marchingTroops.find((troop) => getCaptureTroopCommanderId(troop) === profileCommanderId);
    if (matched) return matched;
    if (getCommanderProfileId(profile)) return null;
  }
  if (getCommanderProfileId(profile)) return null;
  const sideCommanderId = toNonNegativeInt(targetSide === "defender" ? report.def : report.att, 0);
  if (sideCommanderId > 0) {
    const matched = marchingTroops.find((troop) => getCaptureTroopCommanderId(troop) === sideCommanderId);
    if (matched) return matched;
  }
  return targetSide === "defender"
    ? (marchingTroops[1] || marchingTroops[0] || null)
    : (marchingTroops[0] || null);
};

const getCaptureUserView = (payload = null, troop = null, commanderId = 0) => {
  const report = payload && typeof payload === "object" ? payload : {};
  const normalizedCommanderId = toNonNegativeInt(commanderId, getCaptureTroopCommanderId(troop));
  const users = report.users && typeof report.users === "object" ? report.users : {};
  const directUser = normalizedCommanderId > 0 && users[String(normalizedCommanderId)]
    && typeof users[String(normalizedCommanderId)] === "object"
    ? users[String(normalizedCommanderId)]
    : null;
  return clonePlainObject(directUser || troop?.u_v || {});
};

const normalizeCaptureTroopForPairing = (troop = null, payload = null, side = "attacker") => {
  const clonedTroop = clonePlainObject(troop);
  const commanderId = getCaptureTroopCommanderId(clonedTroop);
  const allianceId = getCaptureTroopAllianceId(clonedTroop);
  const userView = getCaptureUserView(payload, clonedTroop, commanderId);
  if (commanderId > 0) {
    clonedTroop.uid = commanderId;
    userView.id = toNonNegativeInt(userView.id, commanderId);
  }
  if (allianceId > 0) {
    clonedTroop.asid = allianceId;
    userView.asid = toNonNegativeInt(userView.asid, allianceId);
  }
  clonedTroop.is_att = side !== "defender";
  clonedTroop.u_v = userView;
  return {
    troop: clonedTroop,
    commanderId,
    allianceId,
    userView,
  };
};

const buildLinkedCapturePairBattleInput = ({
  attackerBaseline = null,
  defenderBaseline = null,
  attackerProfile = null,
  defenderProfile = null,
  selectedBattleType = "uranium_mine",
} = {}) => {
  const attackerPayload = getAction20019PayloadFromBaseline(attackerBaseline);
  const defenderPayload = getAction20019PayloadFromBaseline(defenderBaseline);
  if (!attackerPayload || !defenderPayload) return null;
  const attackerTroop = getCaptureTroopForProfile(attackerPayload, attackerProfile, "attacker");
  const defenderTroop = getCaptureTroopForProfile(defenderPayload, defenderProfile, "defender");
  if (!attackerTroop || !defenderTroop) return null;

  const pairedAttacker = normalizeCaptureTroopForPairing(attackerTroop, attackerPayload, "attacker");
  const pairedDefender = normalizeCaptureTroopForPairing(defenderTroop, defenderPayload, "defender");
  if (pairedAttacker.commanderId <= 0 || pairedDefender.commanderId <= 0) return null;

  const battleInput = clonePlainObject(attackerPayload);
  battleInput.id = pairedAttacker.commanderId;
  battleInput.att = pairedAttacker.commanderId;
  battleInput.def = pairedDefender.commanderId;
  battleInput.a_aid = pairedAttacker.allianceId || toNonNegativeInt(battleInput.a_aid, 0);
  battleInput.d_aid = pairedDefender.allianceId || toNonNegativeInt(defenderPayload.d_aid, 0) || toNonNegativeInt(battleInput.d_aid, 0);
  battleInput.bt = Number.isFinite(Number(attackerPayload.bt))
    ? attackerPayload.bt
    : (selectedBattleType === "uranium_mine" ? 1 : 0);
  battleInput.p_r = Number.isFinite(Number(attackerPayload.p_r)) ? attackerPayload.p_r : 200;
  battleInput.battleType = Number(battleInput.bt) === 1
    ? "uranium_mine"
    : (selectedBattleType || "nation_siege");
  battleInput.users = {
    [String(pairedAttacker.commanderId)]: pairedAttacker.userView,
    [String(pairedDefender.commanderId)]: pairedDefender.userView,
  };
  battleInput.marchingTroops = [pairedAttacker.troop, pairedDefender.troop];
  battleInput.enteringTroops = [];
  battleInput.joinedTroopDataList = [];
  battleInput.retreatingUsers = [];
  battleInput.retreatedUserSoldiers = {};
  battleInput.commanderProfiles = undefined;
  battleInput.attackerStacks = undefined;
  battleInput.defenderStacks = undefined;
  if (battleInput.snap && typeof battleInput.snap === "object") {
    battleInput.snap = { ...battleInput.snap, r_s: toNonNegativeInt(battleInput.snap.r_s, 0) };
  }
  return battleInput;
};

const normalizeReplayTroopRows = (rawWarrs = []) => {
  const byGid = new Map();
  (Array.isArray(rawWarrs) ? rawWarrs : []).forEach((entry) => {
    const source = entry?.so && typeof entry.so === "object" ? entry.so : entry;
    if (!source || typeof source !== "object") return;
    Object.entries(source).forEach(([rawGid, rawCount]) => {
      const troopGid = toNonNegativeInt(rawGid, 0);
      const count = toNonNegativeInt(rawCount, 0);
      if (troopGid <= 0 || count <= 0) return;
      byGid.set(troopGid, toNonNegativeInt(byGid.get(troopGid), 0) + count);
    });
  });
  return Array.from(byGid.entries()).map(([troopGid, count]) => ({ troopGid, count }));
};

const inferReplayParticipantSide = (troop = {}, report = {}) => {
  if (troop?.is_att === true) return "attacker";
  if (troop?.is_att === false) return "defender";
  const uid = toNonNegativeInt(troop?.uid || troop?.u_v?.id, 0);
  if (uid && uid === toNonNegativeInt(report?.att, 0)) return "attacker";
  if (uid && uid === toNonNegativeInt(report?.def, 0)) return "defender";
  return "unknown";
};

const buildReportViewFromReplayPayload = (baseline = null) => {
  const report = baseline?.reportPayload && typeof baseline.reportPayload === "object"
    ? baseline.reportPayload
    : {};
  const participants = (Array.isArray(report?.marchingTroops) ? report.marchingTroops : [])
    .map((troop, index) => {
      const userView = troop?.u_v && typeof troop.u_v === "object" ? troop.u_v : {};
      const troops = normalizeReplayTroopRows(troop?.warrs);
      const totalUnits = troops.reduce((sum, row) => sum + toNonNegativeInt(row?.count, 0), 0);
      if (!troops.length) return null;
      return {
        sourceIndex: index,
        side: inferReplayParticipantSide(troop, report),
        gameCommanderId: toNonNegativeInt(userView?.id || troop?.uid, 0) || null,
        name: String(userView?.nick || "").trim(),
        allianceShort: String(userView?.asn || "").trim(),
        allianceGameId: toNonNegativeInt(troop?.asid || userView?.asid, 0) || null,
        cityLevel: toNonNegativeInt(userView?.ct_lvl, 0) || null,
        commanderLevel: toNonNegativeInt(userView?.lvl, 0) || null,
        troops,
        totalUnits,
      };
    })
    .filter(Boolean);
  if (!participants.length) return null;
  return buildBattleLogReportView({ participants })?.battleReport || null;
};

const buildCapturedReportView = (baseline = null) => {
  if (!baseline || typeof baseline !== "object") return null;
  const truth = baseline?.captureTruth && typeof baseline.captureTruth === "object"
    ? baseline.captureTruth
    : {};
  const attackerTroops = Array.isArray(truth.attackerTroops) ? truth.attackerTroops : [];
  const defenderTroops = Array.isArray(truth.defenderTroops) ? truth.defenderTroops : [];
  const attackerSummary = truth.attackerSummary && typeof truth.attackerSummary === "object"
    ? truth.attackerSummary
    : summarizeReportRows(attackerTroops);
  const defenderSummary = truth.defenderSummary && typeof truth.defenderSummary === "object"
    ? truth.defenderSummary
    : summarizeReportRows(defenderTroops);
  if (!attackerTroops.length && !defenderTroops.length) {
    return buildReportViewFromReplayPayload(baseline);
  }
  return {
    attackerSummary,
    defenderSummary,
    attackerTroops,
    defenderTroops,
  };
};

const parsePairArrayToMap = (rawArray, sink = new Map()) => {
  if (!Array.isArray(rawArray)) return sink;
  for (let i = 0; i < rawArray.length - 1; i += 2) {
    const effectId = Math.trunc(Number(rawArray[i]));
    const value = Number(rawArray[i + 1]);
    if (!Number.isFinite(effectId) || effectId <= 0 || !Number.isFinite(value)) continue;
    sink.set(effectId, Number(sink.get(effectId) || 0) + value);
  }
  return sink;
};

const roundBattleReportBonusValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round(numeric * 10) / 10;
};

const hasBattleReportBonusValue = (value, percentValue) => (
  Math.abs(Number(value) || 0) >= 0.05 || Math.abs(Number(percentValue) || 0) >= 0.05
);

const rateValueToPercent = (rawValue) => {
  const value = Math.max(0, Number(rawValue) || 0);
  if (value <= 0) return 0;
  return (value / (1000 + value)) * 100;
};

const buildPreviewTechMapFromProfile = (profile = null) => {
  const snapshotArrays = profile?.battleReportSnapshot?.parityArrays && typeof profile.battleReportSnapshot.parityArrays === "object"
    ? profile.battleReportSnapshot.parityArrays
    : {};
  const parityProfile = profile?.parityProfile && typeof profile.parityProfile === "object"
    ? profile.parityProfile
    : {};
  const techMap = new Map();
  ["ba_tech", "ba_h_tech", "sw_sp_eff", "cw_sp_eff"].forEach((fieldKey) => {
    const sourceArray = Array.isArray(snapshotArrays[fieldKey]) && snapshotArrays[fieldKey].length
      ? snapshotArrays[fieldKey]
      : parityProfile[fieldKey];
    parsePairArrayToMap(sourceArray, techMap);
  });
  return techMap;
};

const isRawEffectAggregateRow = (row = {}) => {
  const label = String(row?.label || "");
  const key = String(row?.key || "");
  return /effect\s*#\d+/i.test(label)
    || /^ba_tech_\d+$/i.test(key)
    || /^ba_h_tech_\d+$/i.test(key);
};

const buildPreviewBonusRowsForProfile = (profile = null) => {
  const aggregateRows = Array.isArray(profile?.battleReportSnapshot?.aggregateRows)
    ? profile.battleReportSnapshot.aggregateRows
    : [];
  const legacyRows = aggregateRows.filter((row) => !isRawEffectAggregateRow(row));
  const techMap = buildPreviewTechMapFromProfile(profile);
  const computedRows = [];

  PREVIEW_BONUS_METRICS.forEach((metric) => {
    const value = roundBattleReportBonusValue(metric.compute(techMap));
    const percent = typeof metric.computePercent === "function"
      ? roundBattleReportBonusValue(metric.computePercent(techMap))
      : null;
    if (!hasBattleReportBonusValue(value, percent)) return;
    computedRows.push({
      key: metric.key,
      label: metric.label,
      format: metric.format,
      value,
      percent,
    });
  });

  if (computedRows.length > 0) {
    return [...legacyRows, ...computedRows];
  }
  return legacyRows;
};

const isExcludedFromTroopLimit = (troopId, excludes = []) => (
  new Set((Array.isArray(excludes) ? excludes : []).map(String)).has(String(troopId))
);

const normalizeCombatProfiles = (troopDefinitions = [], incoming = {}, options = {}) => {
  void options;
  const normalized = { ...(incoming || {}) };

  troopDefinitions.forEach((troop) => {
    const existing = normalized[troop.id];
    if (existing && typeof existing === "object") {
      normalized[troop.id] = { ...existing };
      return;
    }

    normalized[troop.id] = {
      tiers: {
        1: {
          ...DEFAULT_TIER_STATS,
          attackRange: ROW_ATTACK_RANGE[troop.row] ?? DEFAULT_TIER_STATS.attackRange,
          attack: Math.round((ROW_DAMAGE_FACTOR[troop.row] || 0.04) * 3500),
        },
      },
    };
  });

  return normalized;
};

const getTierOptionsByTroop = (troopDefinitions = [], combatProfiles = {}) => {
  const result = {};
  troopDefinitions.forEach((troop) => {
    const profile = combatProfiles?.[troop.id] || {};

    const evolutionTimeline = profile?.evolution?.timeline || [];
    if (evolutionTimeline.length) {
      const evolutionOptions = evolutionTimeline
        .map((row) => String(row?.phaseAndLevel || ""))
        .filter((v) => v && v !== "undefined:undefined");
      result[troop.id] = [...new Set(evolutionOptions)];
      return;
    }

    const explicitTiers = Object.keys(profile?.tiers || {})
      .map((v) => Number(v))
      .filter(Number.isFinite);

    const titanTableTiers = Object.keys(profile?.tierStatsByTierAndLevel || {})
      .map((v) => Number(v))
      .filter(Number.isFinite);

    result[troop.id] = [...new Set([...explicitTiers, ...titanTableTiers])].sort((a, b) => a - b);
  });
  return result;
};

const getLevelOptionsByTroop = (troopDefinitions = [], combatProfiles = {}) => {
  const result = {};
  troopDefinitions.forEach((troop) => {
    const levelOptions = (combatProfiles?.[troop.id]?.levelProgression || [])
      .map((row) => Number(row?.titanLevel))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    result[troop.id] = levelOptions;
  });
  return result;
};

const getTierOptionLabelsByTroop = (troopDefinitions = [], combatProfiles = {}) => {
  const result = {};
  troopDefinitions.forEach((troop) => {
    const labels = {};
    const profile = combatProfiles?.[troop.id] || {};

    const evolutionTimeline = profile?.evolution?.timeline || [];
    if (evolutionTimeline.length) {
      evolutionTimeline.forEach((row) => {
        const value = String(row?.phaseAndLevel || "");
        if (!value) return;
        const match = value.match(/^(\d+):(\d+)$/);
        labels[value] = match ? `Phase ${match[1]} Lv ${match[2]}` : value;
      });
      result[troop.id] = labels;
      return;
    }

    (Object.entries(profile?.tiers || {})).forEach(([tier, row]) => {
      const explicit = String(row?.tierLabel || "").trim();
      labels[String(tier)] = explicit || `T${tier}`;
    });
    result[troop.id] = labels;
  });
  return result;
};

const getLevelOptionLabelsByTroop = (troopDefinitions = [], combatProfiles = {}) => {
  const result = {};
  troopDefinitions.forEach((troop) => {
    const rows = combatProfiles?.[troop.id]?.levelProgression || [];
    const labels = {};
    rows.forEach((row) => {
      const lvl = Number(row?.titanLevel);
      if (!Number.isFinite(lvl)) return;
      labels[String(lvl)] = `Level ${lvl + 1}`;
    });
    result[troop.id] = labels;
  });
  return result;
};

const createTierSelectionState = (troopDefinitions = [], tierOptionsByTroop = {}) => {
  const state = {};
  troopDefinitions.forEach((troop) => {
    const options = tierOptionsByTroop[troop.id] || [];
    state[troop.id] = options.length ? options[options.length - 1] : "";
  });
  return state;
};

const createLevelSelectionState = (troopDefinitions = [], levelOptionsByTroop = {}) => {
  const state = {};
  troopDefinitions.forEach((troop) => {
    const options = levelOptionsByTroop[troop.id] || [];
    state[troop.id] = options.length ? options[options.length - 1] : "";
  });
  return state;
};

const parseTierSelection = (selectedTier, profile = {}, fallbackLevelRow = {}) => {
  const rawValue = selectedTier == null ? "" : String(selectedTier);
  const evolutionTimeline = profile?.evolution?.timeline || [];
  const evolutionRow = evolutionTimeline.find((row) => String(row?.phaseAndLevel || "") === rawValue) || null;

  const fallbackPhase = fallbackLevelRow?.tierPhase || fallbackLevelRow?.titanTierPhase || "";
  const fallbackEvoLevel = Number(fallbackLevelRow?.tierLevel ?? fallbackLevelRow?.titanTierLevel ?? 0);

  const phase = evolutionRow?.phase || fallbackPhase;
  const evolutionLevel = Number(
    evolutionRow?.evolutionLevel
      ?? (Number.isFinite(fallbackEvoLevel) ? fallbackEvoLevel : 0),
  );

  return {
    rawValue,
    evolutionRow,
    phase,
    evolutionLevel,
  };
};

const pickNearestStat = (rows = [], level = 1, fieldNames = []) => {
  const sorted = [...rows]
    .filter((row) => Number.isFinite(Number(row?.titanLevel)))
    .sort((a, b) => Number(a.titanLevel) - Number(b.titanLevel));

  const atOrBelow = sorted.filter((row) => Number(row.titanLevel) <= Number(level));
  const search = atOrBelow.length ? atOrBelow.reverse() : sorted.reverse();
  for (const row of search) {
    for (const field of fieldNames) {
      const value = Number(row?.[field]);
      if (Number.isFinite(value)) return value;
    }
  }
  return 0;
};

const getTierStats = (troopId, selectedTier, selectedLevel, combatProfiles, fallbackRow) => {
  const profile = combatProfiles?.[troopId] || {};

  if (Array.isArray(profile.levelProgression) && profile.levelProgression.length) {
    const numericLevel = Number(selectedLevel || profile.levelProgression[profile.levelProgression.length - 1]?.titanLevel || 1);
    const levelRow = profile.levelProgression.find((row) => Number(row?.titanLevel) === numericLevel)
      || profile.levelProgression[profile.levelProgression.length - 1]
      || {};

    const tierSelection = parseTierSelection(selectedTier, profile, levelRow);

    const levelRows = profile.levelProgression || [];
    const baseAttack = pickNearestStat(levelRows, numericLevel, ["attackCumulative", "attack"]);
    const baseHp = pickNearestStat(levelRows, numericLevel, ["hpCumulative", "hp"]);
    const baseDefense = pickNearestStat(levelRows, numericLevel, ["defenseCumulative", "defense"]);

    const phaseLevel = Number(tierSelection.evolutionRow?.titanLevel || numericLevel || 1);
    const phaseAttack = pickNearestStat(levelRows, phaseLevel, ["phaseAttackCumulative", "phaseAttack"]);
    const phaseHp = pickNearestStat(levelRows, phaseLevel, ["phaseHpCumulative", "phaseHp"]);
    const phaseDefense = pickNearestStat(levelRows, phaseLevel, ["phaseDefenseCumulative", "phaseDefense"]);

    // Titan/warplane model: selected Level cumulative + selected Evolution cumulative.
    const attack = baseAttack + phaseAttack;
    const hp = baseHp + phaseHp;
    const defense = baseDefense + phaseDefense;

    return {
      ...DEFAULT_TIER_STATS,
      ...levelRow,
      hp: hp || DEFAULT_TIER_STATS.hp,
      attack: attack || DEFAULT_TIER_STATS.attack,
      defense: defense || DEFAULT_TIER_STATS.defense,
      attackCooldown: Number(profile.attackCooldown || DEFAULT_TIER_STATS.attackCooldown),
      attackRange: Number(profile.attackRange || ROW_ATTACK_RANGE[fallbackRow] || DEFAULT_TIER_STATS.attackRange),
      units: Number(profile.units || DEFAULT_TIER_STATS.units),
      selectedTier: tierSelection.rawValue,
      selectedLevel: numericLevel,
      selectedEvolutionPhase: tierSelection.phase,
      selectedEvolutionLevel: tierSelection.evolutionLevel,
    };
  }

  const tierKey = String(selectedTier ?? "");
  const tiers = profile?.tiers || {};
  const picked = tiers[tierKey] || tiers[Object.keys(tiers)[0]];
  const defaultStats = profile?.defaultStats && typeof profile.defaultStats === "object"
    ? profile.defaultStats
    : {};

  return {
    ...DEFAULT_TIER_STATS,
    ...defaultStats,
    ...(picked || {}),
    attackRange: Number(
      picked?.attackRange
      ?? defaultStats?.attackRange
      ?? ROW_ATTACK_RANGE[fallbackRow]
      ?? DEFAULT_TIER_STATS.attackRange
    ),
    moveSpeed: Number(picked?.moveSpeed ?? defaultStats?.moveSpeed ?? 0.8),
    troopGid: Number(
      picked?.troopGid
      ?? profile?.defaultTroopGid
      ?? 0
    ) || null,
    specialSkill: picked?.specialSkill || profile?.specialSkillSummary || null,
  };
};

const getTroopUnitSize = (troopId, tierByTroop = {}, levelByTroop = {}, troopDefinitions = [], combatProfiles = {}) => {
  const troop = troopDefinitions.find((entry) => entry.id === troopId);
  if (!troop) return 1;
  const stats = getTierStats(troopId, tierByTroop?.[troopId], levelByTroop?.[troopId], combatProfiles, troop.row);
  const units = Number(stats?.units || 1);
  return Number.isFinite(units) && units > 0 ? units : 1;
};

const sumFleetUnitsExcluding = (
  army = {},
  excludes = [],
  tierByTroop = {},
  levelByTroop = {},
  troopDefinitions = [],
  combatProfiles = {},
) => {
  const excludeSet = new Set((Array.isArray(excludes) ? excludes : []).map(String));
  return Object.entries(army).reduce((sum, [troopId, count]) => {
    if (excludeSet.has(String(troopId))) return sum;
    const unitSize = getTroopUnitSize(troopId, tierByTroop, levelByTroop, troopDefinitions, combatProfiles);
    return sum + (Number(count || 0) * unitSize);
  }, 0);
};

const buildLimitsFromArmy = (
  army = {},
  excludes = ["zombie"],
  tierByTroop = {},
  levelByTroop = {},
  troopDefinitions = [],
  combatProfiles = {},
) => ({
  zombieLimit: Number(army.zombie || 0),
  troopLimit: sumFleetUnitsExcluding(army, excludes, tierByTroop, levelByTroop, troopDefinitions, combatProfiles),
});

const buildDefaultPresetFromTroops = (troopDefinitions = []) => (
  (Array.isArray(troopDefinitions) ? troopDefinitions : []).reduce((acc, troop) => {
    const troopId = String(troop?.id || "");
    if (!troopId) return acc;
    acc[troopId] = troop?.inputType === "checkbox" ? 1 : 0;
    return acc;
  }, {})
);

const buildTroopGidLookup = (troopDefinitions = [], combatProfiles = {}) => {
  const lookup = {};
  (Array.isArray(troopDefinitions) ? troopDefinitions : []).forEach((troop) => {
    const troopId = String(troop?.id || "");
    if (!troopId) return;
    const profile = combatProfiles?.[troopId] || {};
    Object.entries(profile?.tiers || {}).forEach(([tierKey, tierRow]) => {
      const troopGid = Number(tierRow?.troopGid || 0);
      if (!Number.isFinite(troopGid) || troopGid <= 0) return;
      lookup[String(Math.trunc(troopGid))] = {
        troopId,
        tier: Number.isFinite(Number(tierKey)) ? Number(tierKey) : tierKey,
      };
    });
    const defaultTroopGid = Number(profile?.defaultTroopGid || troop?.defaultTroopGid || 0);
    if (Number.isFinite(defaultTroopGid) && defaultTroopGid > 0 && !lookup[String(Math.trunc(defaultTroopGid))]) {
      lookup[String(Math.trunc(defaultTroopGid))] = { troopId, tier: "" };
    }
  });
  return lookup;
};

const buildTroopDisplayByGid = (troopDefinitions = [], combatProfiles = {}) => {
  const lookup = {};
  (Array.isArray(troopDefinitions) ? troopDefinitions : []).forEach((troop) => {
    const troopId = String(troop?.id || "");
    if (!troopId) return;
    const baseLabel = String(troop?.label || troopId);
    const baseIcon = String(troop?.iconPath || "");
    const baseOrder = Number(troop?.battleOrder || troop?.orderInRow || 999999);
    const profile = combatProfiles?.[troopId] || {};
    const defaultTroopGid = toNonNegativeInt(profile?.defaultTroopGid || troop?.defaultTroopGid, 0);
    const defaultUnitsPerTroop = Math.max(
      1,
      toNonNegativeInt(
        profile?.defaultStats?.units
        ?? profile?.units
        ?? troop?.units,
        1,
      ),
    );
    if (defaultTroopGid > 0 && !lookup[String(defaultTroopGid)]) {
      lookup[String(defaultTroopGid)] = {
        troopId,
        label: baseLabel,
        iconPath: baseIcon,
        reportOrder: baseOrder,
        unitsPerTroop: defaultUnitsPerTroop,
      };
    }
    Object.entries(profile?.tiers || {}).forEach(([tierKey, tierRow]) => {
      const troopGid = toNonNegativeInt(tierRow?.troopGid, 0);
      if (troopGid <= 0) return;
      const tierUnitsPerTroop = Math.max(
        1,
        toNonNegativeInt(
          tierRow?.units
          ?? profile?.defaultStats?.units
          ?? defaultUnitsPerTroop,
          1,
        ),
      );
      lookup[String(troopGid)] = {
        troopId,
        label: String(tierRow?.troopName || tierRow?.tierLabel || baseLabel),
        iconPath: baseIcon,
        reportOrder: baseOrder + (Number(tierKey) / 100),
        unitsPerTroop: tierUnitsPerTroop,
      };
    });
  });
  return lookup;
};

const canonicalizeTitanTroopDefinition = (troop = {}) => {
  const normalized = { ...(troop || {}) };
  const wtype = Math.trunc(Number(normalized?.wtype || 0));
  if (wtype === TITAN_WTYPE_EMPRESS) {
    return {
      ...normalized,
      id: "femaleTitan",
      label: "Female Titan",
      iconPath: "/battle-sim-icons/femaleTitan.png",
    };
  }
  if (wtype === TITAN_WTYPE_BISON) {
    return {
      ...normalized,
      id: "maleTitan",
      label: "Male Titan",
      iconPath: "/battle-sim-icons/maleTitan.png",
    };
  }
  return normalized;
};

const canonicalizeTitanCombatProfiles = (profiles = {}, rawTroopDefinitions = []) => {
  const source = profiles && typeof profiles === "object" ? profiles : {};
  const out = { ...source };
  let empressProfileKey = "";
  let bisonProfileKey = "";
  (Array.isArray(rawTroopDefinitions) ? rawTroopDefinitions : []).forEach((troop) => {
    const troopId = String(troop?.id || "").trim();
    if (!troopId) return;
    const wtype = Math.trunc(Number(troop?.wtype || 0));
    if (wtype === TITAN_WTYPE_EMPRESS) empressProfileKey = troopId;
    if (wtype === TITAN_WTYPE_BISON) bisonProfileKey = troopId;
  });
  if (empressProfileKey && empressProfileKey !== "femaleTitan" && source[empressProfileKey] && !out.femaleTitan) {
    out.femaleTitan = source[empressProfileKey];
  }
  if (bisonProfileKey && bisonProfileKey !== "maleTitan" && source[bisonProfileKey] && !out.maleTitan) {
    out.maleTitan = source[bisonProfileKey];
  }
  return out;
};

const seedArmyFromTroopPresetRows = (troopDefinitions = [], troopGidLookup = {}, rows = []) => {
  const army = createArmyState(troopDefinitions);
  const tierByTroop = {};
  let matchedRowCount = 0;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const troopGid = toNonNegativeInt(row?.troopGid, 0);
    const count = toNonNegativeInt(row?.count, 0);
    if (!troopGid || !count) return;
    const match = troopGidLookup?.[String(troopGid)] || null;
    if (!match?.troopId) return;
    const troopId = String(match.troopId);
    army[troopId] = toNonNegativeInt(army[troopId], 0) + count;
    if (match.tier !== "" && match.tier !== null && match.tier !== undefined) {
      tierByTroop[troopId] = match.tier;
    }
    matchedRowCount += 1;
  });
  return { army, tierByTroop, matchedRowCount };
};

const seedArmyFromCaptureTroop = (troopDefinitions = [], troopGidLookup = {}, troop = null) => (
  seedArmyFromTroopPresetRows(troopDefinitions, troopGidLookup, normalizeReplayTroopRows(troop?.warrs))
);

const getCommanderProfileId = (profile = null) => String(profile?._id || "").trim();

const isCapturedCurrentProfile = (profile = null) => (
  String(profile?.sourceType || profile?.profileKind || "").trim() === "captured_current"
  || profile?.isCapturedCurrent === true
);

const getCommanderProfileSourceLabel = (profile = null) => (
  isCapturedCurrentProfile(profile) ? "Captured current" : "Manual profile"
);

const getCommanderTroopSourceSignature = (profile = null) => {
  const profileId = getCommanderProfileId(profile);
  if (!profileId) return "";
  const baselineId = String(profile?.linkedReportBaselineId || "").trim();
  if (baselineId) return `capture:${profileId}:${baselineId}`;
  const preset = profile?.defaultTroopPreset;
  const rows = Array.isArray(preset?.rows) ? preset.rows : [];
  if (!rows.length) return "";
  return `preset:${profileId}:${String(preset?.capturedAtUtc || "")}:${rows.length}:${toNonNegativeInt(preset?.totalUnits, 0)}`;
};

const getCustomCommanderTroopSourceSignature = (profile = null) => {
  const profileId = getCommanderProfileId(profile);
  return profileId ? `custom:${profileId}` : "";
};

const normalizePresetSides = (presetGroup = {}, defaultPreset = {}) => {
  const attacker = presetGroup?.attacker && typeof presetGroup.attacker === "object"
    ? presetGroup.attacker
    : {};
  const defender = presetGroup?.defender && typeof presetGroup.defender === "object"
    ? presetGroup.defender
    : {};
  return {
    attacker: { ...defaultPreset, ...attacker },
    defender: { ...defaultPreset, ...defender },
  };
};

const normalizeConfig = (incoming = {}) => {
  const rawTroopDefinitions = Array.isArray(incoming?.troopDefinitions) ? incoming.troopDefinitions : [];
  const troopDefinitions = rawTroopDefinitions.map((troop) => canonicalizeTitanTroopDefinition(troop));
  if (!troopDefinitions.length) {
    throw new Error("Battle simulation config is missing extracted troop definitions.");
  }

  const rowOrder = (Array.isArray(incoming?.rowOrder) && incoming.rowOrder.length)
    ? incoming.rowOrder
    : DEFAULT_BATTLE_SIM_CONFIG.rowOrder;
  const incomingProfiles = canonicalizeTitanCombatProfiles(incoming?.combatProfiles || {}, rawTroopDefinitions);
  const combatProfiles = normalizeCombatProfiles(
    troopDefinitions,
    incomingProfiles,
    { preferIncoming: true },
  );
  const defaultPreset = buildDefaultPresetFromTroops(troopDefinitions);
  const attackerPreset = incoming?.presets?.attacker && typeof incoming.presets.attacker === "object"
    ? incoming.presets.attacker
    : defaultPreset;
  const defenderPreset = incoming?.presets?.defender && typeof incoming.presets.defender === "object"
    ? incoming.presets.defender
    : defaultPreset;

  const normalizedPresets = {
    attacker: { ...defaultPreset, ...attackerPreset },
    defender: { ...defaultPreset, ...defenderPreset },
    basic: normalizePresetSides(incoming?.presets?.basic || {}, defaultPreset),
  };

  const incomingPresetGroups = incoming?.presets && typeof incoming.presets === "object"
    ? incoming.presets
    : {};
  Object.entries(incomingPresetGroups).forEach(([presetKey, presetGroup]) => {
    if (presetKey === "attacker" || presetKey === "defender" || presetKey === "basic") return;
    if (!presetGroup || typeof presetGroup !== "object") return;
    normalizedPresets[presetKey] = normalizePresetSides(presetGroup, defaultPreset);
  });

  return {
    version: Number(incoming?.version || 1),
    updatedAt: String(incoming?.updatedAt || ""),
    troopDefinitions,
    combatProfiles,
    rowOrder,
    rowLabels: {
      ...DEFAULT_BATTLE_SIM_CONFIG.rowLabels,
      ...(incoming?.rowLabels || {}),
    },
    rowGeometry: {
      ...DEFAULT_BATTLE_SIM_CONFIG.rowGeometry,
      ...(incoming?.rowGeometry || {}),
    },
    rowLayouts: {
      ...DEFAULT_BATTLE_SIM_CONFIG.rowLayouts,
      ...(incoming?.rowLayouts || {}),
    },
    layoutRules: {
      ...DEFAULT_BATTLE_SIM_CONFIG.layoutRules,
      ...(incoming?.layoutRules || {}),
      presenceOverrides: {
        ...DEFAULT_BATTLE_SIM_CONFIG.layoutRules.presenceOverrides,
        ...(incoming?.layoutRules?.presenceOverrides || {}),
      },
      presenceOrderOverrides: {
        ...DEFAULT_BATTLE_SIM_CONFIG.layoutRules.presenceOrderOverrides,
        ...(incoming?.layoutRules?.presenceOrderOverrides || {}),
      },
    },
    limitsPolicy: {
      ...DEFAULT_BATTLE_SIM_CONFIG.limitsPolicy,
      ...(incoming?.limitsPolicy || {}),
    },
    presets: normalizedPresets,
    troopCatalog: incoming?.troopCatalog || null,
  };
};

const buildPendingReportForSide = ({
  sideArmy = {},
  sideTierByTroop = {},
  sideLevelByTroop = {},
  troopDefinitions = [],
  combatProfiles = {},
  troopDisplayByGid = {},
  profile = null,
}) => {
  const reportContext = profile?.battleReportSnapshot?.reportContext && typeof profile.battleReportSnapshot.reportContext === "object"
    ? profile.battleReportSnapshot.reportContext
    : null;
  const rawSpecialSource = reportContext?.specialArmyDamageSource && typeof reportContext.specialArmyDamageSource === "object"
    ? reportContext.specialArmyDamageSource
    : null;
  const sourceAttribution = {
    available: Boolean(rawSpecialSource && Object.keys(rawSpecialSource).length > 0),
    entryCount: rawSpecialSource ? Object.keys(rawSpecialSource).length : 0,
  };

  const snapshotRows = Array.isArray(profile?.battleReportSnapshot?.troopReportRows)
    ? profile.battleReportSnapshot.troopReportRows
    : [];
  const hasCapturedTruthRows = snapshotRows.length > 0;
  const rows = [];

  if (hasCapturedTruthRows) {
    snapshotRows.forEach((row) => {
      const troopGid = toNonNegativeInt(row?.troopGid, 0);
      if (!troopGid) return;
      const meta = troopDisplayByGid?.[String(troopGid)] || {};
      const initialUnits = toNonNegativeInt(row?.initialUnits, 0);
      const lostUnits = row?.lostUnits === null || row?.lostUnits === undefined
        ? null
        : toNonNegativeInt(row?.lostUnits, 0);
      const survivingUnits = row?.survivingUnits === null || row?.survivingUnits === undefined
        ? Math.max(0, initialUnits - toNonNegativeInt(lostUnits, 0))
        : toNonNegativeInt(row?.survivingUnits, 0);
      rows.push({
        troopId: String(meta?.troopId || row?.troopId || ""),
        troopGid,
        troopName: String(meta?.label || row?.troopName || `Troop ${troopGid}`),
        icon: String(meta?.iconPath || row?.icon || ""),
        reportOrder: Number(meta?.reportOrder || row?.reportOrder || 999999),
        survivingUnits,
        woundedUnits: row?.woundedUnits === null || row?.woundedUnits === undefined
          ? null
          : toNonNegativeInt(row?.woundedUnits, 0),
        lostUnits,
        enemyUnitsKilled: row?.enemyUnitsKilled === null || row?.enemyUnitsKilled === undefined
          ? null
          : toNonNegativeInt(row?.enemyUnitsKilled, 0),
        unitsPerTroop: Math.max(1, toNonNegativeInt(meta?.unitsPerTroop, 1)),
      });
    });
  } else {
    (Array.isArray(troopDefinitions) ? troopDefinitions : []).forEach((troop) => {
      const count = toNonNegativeInt(sideArmy?.[troop?.id], 0);
      if (count <= 0) return;
      const stats = getTierStats(
        troop.id,
        sideTierByTroop?.[troop.id],
        sideLevelByTroop?.[troop.id],
        combatProfiles || {},
        troop.row,
      );
      const unitsPerTroop = Math.max(1, toNonNegativeInt(stats?.units, 1));
      rows.push({
        troopId: String(troop?.id || ""),
        troopGid: Number(stats?.troopGid || troop?.defaultTroopGid || 0) || null,
        troopName: String(troop?.label || "Unknown"),
        icon: String(troop?.iconPath || ""),
        reportOrder: Number(troop?.battleOrder || troop?.orderInRow || 999999),
        survivingUnits: count * unitsPerTroop,
        woundedUnits: null,
        lostUnits: null,
        enemyUnitsKilled: null,
      });
    });
  }
  rows.sort((left, right) => (
    Number(left?.reportOrder || 999999) - Number(right?.reportOrder || 999999)
    || Number(left?.troopGid || Number.MAX_SAFE_INTEGER) - Number(right?.troopGid || Number.MAX_SAFE_INTEGER)
  ));
  const totalUnits = rows.reduce((sum, row) => sum + toNonNegativeInt(row?.survivingUnits, 0), 0);
  const fallbackSummary = {
    battlers: totalUnits > 0 ? 1 : 0,
    totalUnits,
    enemyUnitsKilled: null,
    lostUnits: null,
    woundedUnits: null,
    survivingUnits: totalUnits,
  };
  const snapshotSummary = profile?.battleReportSnapshot?.summary && typeof profile.battleReportSnapshot.summary === "object"
    ? profile.battleReportSnapshot.summary
    : null;
  const snapshotTotalUnits = toNonNegativeInt(snapshotSummary?.totalUnits, -1);
  const effectiveTotalUnits = snapshotTotalUnits >= 0 ? snapshotTotalUnits : totalUnits;
  const summary = {
    ...fallbackSummary,
    ...(snapshotSummary || {}),
    enemyUnitsKilled: hasCapturedTruthRows
      ? rows.reduce((sum, row) => sum + toNonNegativeInt(row?.enemyUnitsKilled, 0), 0)
      : null,
    lostUnits: hasCapturedTruthRows
      ? rows.reduce((sum, row) => sum + toNonNegativeInt(row?.lostUnits, 0), 0)
      : null,
    woundedUnits: hasCapturedTruthRows
      ? rows.reduce((sum, row) => sum + toNonNegativeInt(row?.woundedUnits, 0), 0)
      : null,
    survivingUnits: hasCapturedTruthRows
      ? rows.reduce((sum, row) => sum + toNonNegativeInt(row?.survivingUnits, 0), 0)
      : effectiveTotalUnits,
    totalUnits: hasCapturedTruthRows
      ? rows.reduce((sum, row) => sum + toNonNegativeInt(row?.survivingUnits, 0) + toNonNegativeInt(row?.lostUnits, 0), 0)
      : effectiveTotalUnits,
    battlers: effectiveTotalUnits > 0 ? 1 : 0,
  };
  const aggregateRows = buildPreviewBonusRowsForProfile(profile);
  return {
    summary,
    troops: rows,
    bonuses: aggregateRows,
    hasCapturedTruthRows,
    sourceAttribution,
  };
};

const BattleSimulation = () => {
  const navigate = useNavigate();
  const { isAuthInitializing, isAuthenticated } = useContext(AuthContext);
  const isUserAuthenticated = isAuthenticated();
  const [simulationConfig, setSimulationConfig] = useState(DEFAULT_BATTLE_SIM_CONFIG);
  const [isBattleConfigLoading, setIsBattleConfigLoading] = useState(false);
  const [isBattleConfigLoaded, setIsBattleConfigLoaded] = useState(false);
  const troopDefinitions = useMemo(() => simulationConfig.troopDefinitions || [], [simulationConfig]);

  const excludesFromTroopLimit = useMemo(
    () => simulationConfig?.limitsPolicy?.troopLimitExcludes || ["zombie"],
    [simulationConfig],
  );

  const tierOptionsByTroop = useMemo(
    () => getTierOptionsByTroop(troopDefinitions, simulationConfig.combatProfiles || {}),
    [troopDefinitions, simulationConfig.combatProfiles],
  );

  const levelOptionsByTroop = useMemo(
    () => getLevelOptionsByTroop(troopDefinitions, simulationConfig.combatProfiles || {}),
    [troopDefinitions, simulationConfig.combatProfiles],
  );

  const tierOptionLabelsByTroop = useMemo(
    () => getTierOptionLabelsByTroop(troopDefinitions, simulationConfig.combatProfiles || {}),
    [troopDefinitions, simulationConfig.combatProfiles],
  );

  const levelOptionLabelsByTroop = useMemo(
    () => getLevelOptionLabelsByTroop(troopDefinitions, simulationConfig.combatProfiles || {}),
    [troopDefinitions, simulationConfig.combatProfiles],
  );

  const troopGidLookup = useMemo(
    () => buildTroopGidLookup(troopDefinitions, simulationConfig.combatProfiles || {}),
    [troopDefinitions, simulationConfig.combatProfiles],
  );
  const troopDisplayByGid = useMemo(
    () => buildTroopDisplayByGid(troopDefinitions, simulationConfig.combatProfiles || {}),
    [troopDefinitions, simulationConfig.combatProfiles],
  );

  const [attackerArmy, setAttackerArmy] = useState(() =>
    createArmyState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.presets.attacker),
  );
  const [defenderArmy, setDefenderArmy] = useState(() =>
    createArmyState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.presets.defender),
  );

  const [attackerTierByTroop, setAttackerTierByTroop] = useState(() =>
    createTierSelectionState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, getTierOptionsByTroop(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, normalizeCombatProfiles(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.combatProfiles || {}))),
  );
  const [defenderTierByTroop, setDefenderTierByTroop] = useState(() =>
    createTierSelectionState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, getTierOptionsByTroop(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, normalizeCombatProfiles(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.combatProfiles || {}))),
  );

  const [attackerLevelByTroop, setAttackerLevelByTroop] = useState(() =>
    createLevelSelectionState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, getLevelOptionsByTroop(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, normalizeCombatProfiles(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.combatProfiles || {}))),
  );
  const [defenderLevelByTroop, setDefenderLevelByTroop] = useState(() =>
    createLevelSelectionState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, getLevelOptionsByTroop(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, normalizeCombatProfiles(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.combatProfiles || {}))),
  );

  const [attackerLimits, setAttackerLimits] = useState(() =>
    buildLimitsFromArmy(
      createArmyState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.presets.attacker),
      DEFAULT_BATTLE_SIM_CONFIG.limitsPolicy.troopLimitExcludes,
      attackerTierByTroop,
      attackerLevelByTroop,
      DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions,
      normalizeCombatProfiles(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.combatProfiles || {}),
    ),
  );
  const [defenderLimits, setDefenderLimits] = useState(() =>
    buildLimitsFromArmy(
      createArmyState(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.presets.defender),
      DEFAULT_BATTLE_SIM_CONFIG.limitsPolicy.troopLimitExcludes,
      defenderTierByTroop,
      defenderLevelByTroop,
      DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions,
      normalizeCombatProfiles(DEFAULT_BATTLE_SIM_CONFIG.troopDefinitions, DEFAULT_BATTLE_SIM_CONFIG.combatProfiles || {}),
    ),
  );
  const [armySourceBySide, setArmySourceBySide] = useState({ attacker: "", defender: "" });

  const [serverWinner, setServerWinner] = useState(null);
  const [showDebugOverlay, setShowDebugOverlay] = useState(false);
  const [showFrameActionPanel, setShowFrameActionPanel] = useState(true);
  const [isServerSimulating, setIsServerSimulating] = useState(false);
  const [serverParitySummary, setServerParitySummary] = useState("");
  const [serverParityError, setServerParityError] = useState("");
  const [serverTraceFrames, setServerTraceFrames] = useState([]);
  const [serverTraceMeta, setServerTraceMeta] = useState(null);
  const [serverBattleReport, setServerBattleReport] = useState(null);
  const [serverCompareDelta, setServerCompareDelta] = useState(null);
  const [serverReportDiagnostics, setServerReportDiagnostics] = useState(null);
  const [serverTraceEventsByFrame, setServerTraceEventsByFrame] = useState({});
  const [serverTraceFrameIndex, setServerTraceFrameIndex] = useState(0);
  const [isServerTracePlaying, setIsServerTracePlaying] = useState(false);
  const [serverTraceFps, setServerTraceFps] = useState(DEFAULT_TRACE_FPS);
  const [tracePlaybackSpeed, setTracePlaybackSpeed] = useState(1);
  const [traceProjectionMode, setTraceProjectionMode] = useState(DEFAULT_BATTLE_PROJECTION_MODE);
  const [selectedBattleType, setSelectedBattleType] = useState("uranium_mine");
  const [commanderProfiles, setCommanderProfiles] = useState([]);
  const [isCommanderProfilesLoading, setIsCommanderProfilesLoading] = useState(false);
  const [isCommanderProfileMutating, setIsCommanderProfileMutating] = useState(false);
  const [attackerCommanderProfileId, setAttackerCommanderProfileId] = useState("");
  const [defenderCommanderProfileId, setDefenderCommanderProfileId] = useState("");
  const [commanderPickerState, setCommanderPickerState] = useState({ open: false, side: "attacker" });
  const [commanderProfileQuery, setCommanderProfileQuery] = useState("");
  const [activeBattleSimulationTab, setActiveBattleSimulationTab] = useState("existing_reports");
  const [reportBaselines, setReportBaselines] = useState([]);
  const [selectedReportBaselineId, setSelectedReportBaselineId] = useState("");
  const [selectedReportBaseline, setSelectedReportBaseline] = useState(null);
  const [isReportBaselinesLoading, setIsReportBaselinesLoading] = useState(false);
  const [reportBaselineError, setReportBaselineError] = useState("");
  const [reportBaselineQuery, setReportBaselineQuery] = useState("");
  const tracePlaybackIntervalRef = useRef(null);
  const profilePresetAppliedRef = useRef({ attacker: "", defender: "" });
  const selectedCommanderProfileIdsRef = useRef({ attacker: "", defender: "" });

  const loadSimulationConfig = useCallback(async ({ retryOnUnauthorized = true } = {}) => {
    if (isAuthInitializing || !isUserAuthenticated) return false;
    const apiUrl = API_URL;
    setIsBattleConfigLoading(true);
    try {
      const response = await axiosInstance.get(`${apiUrl}/battle-simulation/config`);
      const nextConfig = normalizeConfig(response?.data || {});

      setSimulationConfig(nextConfig);

      const seededAttacker = createArmyState(nextConfig.troopDefinitions, nextConfig.presets.attacker);
      const seededDefender = createArmyState(nextConfig.troopDefinitions, nextConfig.presets.defender);
      const nextTierOptions = getTierOptionsByTroop(nextConfig.troopDefinitions, nextConfig.combatProfiles || {});
      const nextLevelOptions = getLevelOptionsByTroop(nextConfig.troopDefinitions, nextConfig.combatProfiles || {});
      const nextAttackerTiers = createTierSelectionState(nextConfig.troopDefinitions, nextTierOptions);
      const nextDefenderTiers = createTierSelectionState(nextConfig.troopDefinitions, nextTierOptions);
      const nextAttackerLevels = createLevelSelectionState(nextConfig.troopDefinitions, nextLevelOptions);
      const nextDefenderLevels = createLevelSelectionState(nextConfig.troopDefinitions, nextLevelOptions);

      profilePresetAppliedRef.current = { attacker: "", defender: "" };
      setArmySourceBySide({ attacker: "", defender: "" });
      setAttackerArmy(seededAttacker);
      setDefenderArmy(seededDefender);
      setAttackerTierByTroop(nextAttackerTiers);
      setDefenderTierByTroop(nextDefenderTiers);
      setAttackerLevelByTroop(nextAttackerLevels);
      setDefenderLevelByTroop(nextDefenderLevels);
      setAttackerLimits(buildLimitsFromArmy(
        seededAttacker,
        nextConfig.limitsPolicy.troopLimitExcludes,
        nextAttackerTiers,
        nextAttackerLevels,
        nextConfig.troopDefinitions,
        nextConfig.combatProfiles || {},
      ));
      setDefenderLimits(buildLimitsFromArmy(
        seededDefender,
        nextConfig.limitsPolicy.troopLimitExcludes,
        nextDefenderTiers,
        nextDefenderLevels,
        nextConfig.troopDefinitions,
        nextConfig.combatProfiles || {},
      ));
      setIsBattleConfigLoaded(true);
      return true;
    } catch (error) {
      if (retryOnUnauthorized && Number(error?.response?.status || 0) === 401) {
        await new Promise((resolve) => setTimeout(resolve, 350));
        return loadSimulationConfig({ retryOnUnauthorized: false });
      }
      setServerParityError(
        error?.response?.data?.message
        || error?.message
        || "Failed to load extracted battle simulation config.",
      );
      setIsBattleConfigLoaded(false);
      return false;
    } finally {
      setIsBattleConfigLoading(false);
    }
  }, [isAuthInitializing, isUserAuthenticated]);

  useEffect(() => {
    void loadSimulationConfig();
  }, [loadSimulationConfig]);

  useEffect(() => {
    if (isAuthInitializing) return;
    if (isUserAuthenticated) return;
    setIsBattleConfigLoaded(false);
    setCommanderProfiles([]);
    setAttackerCommanderProfileId("");
    setDefenderCommanderProfileId("");
    setReportBaselines([]);
    setSelectedReportBaselineId("");
    setSelectedReportBaseline(null);
    setReportBaselineError("");
    profilePresetAppliedRef.current = { attacker: "", defender: "" };
    setArmySourceBySide({ attacker: "", defender: "" });
  }, [isAuthInitializing, isUserAuthenticated]);

  const commanderProfileById = useMemo(() => (
    new Map((commanderProfiles || []).map((profile) => [String(profile?._id), profile]))
  ), [commanderProfiles]);

  const attackerCommanderProfile = attackerCommanderProfileId
    ? (commanderProfileById.get(String(attackerCommanderProfileId)) || null)
    : null;
  const defenderCommanderProfile = defenderCommanderProfileId
    ? (commanderProfileById.get(String(defenderCommanderProfileId)) || null)
    : null;

  const setArmySourceForSide = useCallback((side, signature) => {
    setArmySourceBySide((prev) => ({
      ...prev,
      [side]: String(signature || ""),
    }));
  }, []);

  const markCustomArmySourceForSide = useCallback((side) => {
    const profile = side === "defender" ? defenderCommanderProfile : attackerCommanderProfile;
    const signature = getCustomCommanderTroopSourceSignature(profile);
    if (!signature) return;
    setArmySourceForSide(side, signature);
  }, [attackerCommanderProfile, defenderCommanderProfile, setArmySourceForSide]);

  const clearBattleRunState = useCallback(() => {
    setServerBattleReport(null);
    setServerWinner(null);
    setServerParitySummary("");
    setServerCompareDelta(null);
    setServerReportDiagnostics(null);
    setServerTraceFrames([]);
    setServerTraceMeta(null);
    setServerTraceEventsByFrame({});
    setServerTraceFrameIndex(0);
    setIsServerTracePlaying(false);
    setServerParityError("");
  }, []);

  const applySeededProfileArmyForSide = useCallback((side, seeded, signature) => {
    if (!seeded?.matchedRowCount) return false;
    const baseTiers = createTierSelectionState(troopDefinitions, tierOptionsByTroop);
    const baseLevels = createLevelSelectionState(troopDefinitions, levelOptionsByTroop);
    const nextTiers = { ...baseTiers, ...seeded.tierByTroop };
    const nextLevels = { ...baseLevels };
    const nextLimits = buildLimitsFromArmy(
      seeded.army,
      excludesFromTroopLimit,
      nextTiers,
      nextLevels,
      troopDefinitions,
      simulationConfig.combatProfiles || {},
    );
    if (side === "defender") {
      setDefenderTierByTroop(nextTiers);
      setDefenderLevelByTroop(nextLevels);
      setDefenderArmy({ ...seeded.army, zombie: nextLimits.zombieLimit });
      setDefenderLimits(nextLimits);
      profilePresetAppliedRef.current.defender = signature;
      setArmySourceForSide("defender", signature);
    } else {
      setAttackerTierByTroop(nextTiers);
      setAttackerLevelByTroop(nextLevels);
      setAttackerArmy({ ...seeded.army, zombie: nextLimits.zombieLimit });
      setAttackerLimits(nextLimits);
      profilePresetAppliedRef.current.attacker = signature;
      setArmySourceForSide("attacker", signature);
    }
    clearBattleRunState();
    return true;
  }, [
    clearBattleRunState,
    excludesFromTroopLimit,
    levelOptionsByTroop,
    setArmySourceForSide,
    simulationConfig.combatProfiles,
    tierOptionsByTroop,
    troopDefinitions,
  ]);

  const clearArmyForCommanderSelection = useCallback((side) => {
    const empty = createArmyState(troopDefinitions);
    if (side === "defender") {
      setDefenderArmy(empty);
      setDefenderLimits({ zombieLimit: 0, troopLimit: 0 });
      setArmySourceForSide("defender", "");
      return;
    }
    setAttackerArmy(empty);
    setAttackerLimits({ zombieLimit: 0, troopLimit: 0 });
    setArmySourceForSide("attacker", "");
  }, [setArmySourceForSide, troopDefinitions]);

  const validateArmySourceForSide = useCallback((side, profile) => {
    const profileId = getCommanderProfileId(profile);
    if (!profileId) return { ok: false, message: `${side} commander profile is missing.` };
    const actual = String(armySourceBySide?.[side] || "").trim();
    const expected = getCommanderTroopSourceSignature(profile);
    const custom = getCustomCommanderTroopSourceSignature(profile);
    if (expected && actual === expected) return { ok: true };
    if (custom && actual === custom) return { ok: true };
    if (!expected && !actual) return { ok: true };
    if (expected && !actual) {
      return {
        ok: false,
        message: `${side === "attacker" ? "Attacker" : "Defender"} troops are still loading from the selected commander profile. Try again after the troop rows update.`,
      };
    }
    return {
      ok: false,
      message: `${side === "attacker" ? "Attacker" : "Defender"} troops do not match the selected commander. Re-select the commander or adjust the troop setup before simulating.`,
    };
  }, [armySourceBySide]);

  useEffect(() => {
    const nextAttackerId = String(attackerCommanderProfileId || "");
    const nextDefenderId = String(defenderCommanderProfileId || "");
    const previous = selectedCommanderProfileIdsRef.current || { attacker: "", defender: "" };
    if (previous.attacker !== nextAttackerId) {
      profilePresetAppliedRef.current.attacker = "";
      clearArmyForCommanderSelection("attacker");
    }
    if (previous.defender !== nextDefenderId) {
      profilePresetAppliedRef.current.defender = "";
      clearArmyForCommanderSelection("defender");
    }
    selectedCommanderProfileIdsRef.current = {
      attacker: nextAttackerId,
      defender: nextDefenderId,
    };
  }, [attackerCommanderProfileId, clearArmyForCommanderSelection, defenderCommanderProfileId]);

  const applyCommanderSelectionFallbacks = (profiles = []) => {
    const firstId = String(profiles?.[0]?._id || "");
    setAttackerCommanderProfileId((prev) => (
      profiles.some((profile) => String(profile?._id) === String(prev))
        ? String(prev)
        : firstId
    ));
    setDefenderCommanderProfileId((prev) => (
      profiles.some((profile) => String(profile?._id) === String(prev))
        ? String(prev)
        : firstId
    ));
  };

  const loadCommanderProfiles = async ({ preferredId = "", retryOnUnauthorized = true } = {}) => {
    if (isAuthInitializing || !isUserAuthenticated) return [];
    setIsCommanderProfilesLoading(true);
    try {
      const [capturedProfiles, manualProfiles] = await Promise.all([
        listCombatProfiles({ limit: 300 }),
        listCommanderProfiles({ limit: 200 }),
      ]);
      let profiles = [
        ...(Array.isArray(capturedProfiles) ? capturedProfiles : []),
        ...(Array.isArray(manualProfiles) ? manualProfiles.map((profile) => ({
          ...profile,
          sourceType: profile?.sourceType || "manual_profile",
          profileKind: profile?.profileKind || "manual_profile",
        })) : []),
      ];
      if (!profiles.length) {
        const created = await createCommanderProfile({
          name: "Default Commander",
          parityProfile: DEFAULT_COMMANDER_PARITY_PROFILE,
          powerSummary: DEFAULT_COMMANDER_POWER_SUMMARY,
          sections: {
            officers: { notes: "", status: "draft", data: { rows: [] } },
            titans: { notes: "", status: "draft" },
            warplane: { notes: "", status: "draft" },
            equipment: { notes: "", status: "draft" },
            antiques: { notes: "", status: "draft" },
            island: { notes: "", status: "draft" },
            biochemicalWorkshop: { notes: "", status: "draft" },
            academy: { notes: "", status: "draft" },
            commanderSkills: { notes: "", status: "draft" },
          },
        });
        profiles = created ? [{ ...created, sourceType: "manual_profile", profileKind: "manual_profile" }] : [];
      }
      setCommanderProfiles(profiles);
      applyCommanderSelectionFallbacks(profiles);
      if (preferredId && profiles.some((profile) => String(profile?._id) === String(preferredId))) {
        if (commanderPickerState.side === "defender") setDefenderCommanderProfileId(String(preferredId));
        else setAttackerCommanderProfileId(String(preferredId));
      }
      return profiles;
    } catch (error) {
      if (retryOnUnauthorized && Number(error?.response?.status || 0) === 401) {
        await new Promise((resolve) => setTimeout(resolve, 350));
        return loadCommanderProfiles({ preferredId, retryOnUnauthorized: false });
      }
      setServerParityError(
        error?.response?.data?.message
        || error?.message
        || "Failed to load commander profiles.",
      );
      return [];
    } finally {
      setIsCommanderProfilesLoading(false);
    }
  };

  const loadReportBaselines = async ({ preferredId = "", retryOnUnauthorized = true } = {}) => {
    if (isAuthInitializing || !isUserAuthenticated) return [];
    setIsReportBaselinesLoading(true);
    setReportBaselineError("");
    try {
      const baselines = await listReportBaselines({ scope: "nation", limit: 200 });
      setReportBaselines(baselines);
      const nextSelectedId = preferredId || selectedReportBaselineId || String(baselines?.[0]?._id || "");
      if (nextSelectedId && baselines.some((baseline) => String(baseline?._id) === String(nextSelectedId))) {
        setSelectedReportBaselineId(String(nextSelectedId));
        setSelectedReportBaseline(baselines.find((baseline) => String(baseline?._id) === String(nextSelectedId)) || null);
      } else {
        setSelectedReportBaselineId("");
        setSelectedReportBaseline(null);
      }
      return baselines;
    } catch (error) {
      if (retryOnUnauthorized && Number(error?.response?.status || 0) === 401) {
        await new Promise((resolve) => setTimeout(resolve, 350));
        return loadReportBaselines({ preferredId, retryOnUnauthorized: false });
      }
      setReportBaselineError(
        error?.response?.data?.message
        || error?.message
        || "Failed to load existing battle reports.",
      );
      return [];
    } finally {
      setIsReportBaselinesLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthInitializing || !isUserAuthenticated) return;
    void loadCommanderProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthInitializing, isUserAuthenticated]);

  useEffect(() => {
    if (isAuthInitializing || !isUserAuthenticated) return;
    void loadReportBaselines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthInitializing, isUserAuthenticated]);

  useEffect(() => {
    if (!commanderPickerState.open) return;
    if (isAuthInitializing || !isUserAuthenticated) return;
    if (isCommanderProfilesLoading) return;
    if (Array.isArray(commanderProfiles) && commanderProfiles.length) return;
    void loadCommanderProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commanderPickerState.open, isAuthInitializing, isUserAuthenticated, isCommanderProfilesLoading, commanderProfiles.length]);

  useEffect(() => {
    const profileId = String(attackerCommanderProfile?._id || "");
    if (!profileId) return;
    if (String(attackerCommanderProfile?.linkedReportBaselineId || "").trim()) return;
    const preset = attackerCommanderProfile?.defaultTroopPreset;
    const rows = Array.isArray(preset?.rows) ? preset.rows : [];
    if (!rows.length) return;
    const presetSignature = getCommanderTroopSourceSignature(attackerCommanderProfile);
    if (profilePresetAppliedRef.current.attacker === presetSignature) return;
    const seeded = seedArmyFromTroopPresetRows(troopDefinitions, troopGidLookup, rows);
    if (!seeded.matchedRowCount) {
      setServerParityError("The selected attacker profile has captured troop rows, but none could be mapped to the current troop catalog.");
      return;
    }
    applySeededProfileArmyForSide("attacker", seeded, presetSignature);
  }, [
    applySeededProfileArmyForSide,
    attackerCommanderProfile,
    excludesFromTroopLimit,
    levelOptionsByTroop,
    tierOptionsByTroop,
    troopDefinitions,
    troopGidLookup,
    simulationConfig.combatProfiles,
  ]);

  useEffect(() => {
    const profileId = String(defenderCommanderProfile?._id || "");
    if (!profileId) return;
    if (String(defenderCommanderProfile?.linkedReportBaselineId || "").trim()) return;
    const preset = defenderCommanderProfile?.defaultTroopPreset;
    const rows = Array.isArray(preset?.rows) ? preset.rows : [];
    if (!rows.length) return;
    const presetSignature = getCommanderTroopSourceSignature(defenderCommanderProfile);
    if (profilePresetAppliedRef.current.defender === presetSignature) return;
    const seeded = seedArmyFromTroopPresetRows(troopDefinitions, troopGidLookup, rows);
    if (!seeded.matchedRowCount) {
      setServerParityError("The selected defender profile has captured troop rows, but none could be mapped to the current troop catalog.");
      return;
    }
    applySeededProfileArmyForSide("defender", seeded, presetSignature);
  }, [
    applySeededProfileArmyForSide,
    defenderCommanderProfile,
    excludesFromTroopLimit,
    levelOptionsByTroop,
    tierOptionsByTroop,
    troopDefinitions,
    troopGidLookup,
    simulationConfig.combatProfiles,
  ]);

  useEffect(() => {
    const profileId = String(attackerCommanderProfile?._id || "");
    const baselineId = String(attackerCommanderProfile?.linkedReportBaselineId || "").trim();
    if (isCapturedCurrentProfile(attackerCommanderProfile)) return;
    if (!profileId || !baselineId) return;
    const signature = `capture:${profileId}:${baselineId}`;
    if (profilePresetAppliedRef.current.attacker === signature) return;
    let cancelled = false;
    const applyCapture = async () => {
      try {
        const baseline = await getReportBaseline(baselineId, { scope: "nation" });
        if (cancelled) return;
        const payload = getAction20019PayloadFromBaseline(baseline);
        const troop = getCaptureTroopForProfile(payload, attackerCommanderProfile, "attacker");
        const seeded = seedArmyFromCaptureTroop(troopDefinitions, troopGidLookup, troop);
        if (!seeded.matchedRowCount) {
          setServerParityError("The selected attacker report baseline was loaded, but no captured attacker troop rows could be mapped.");
          return;
        }
        applySeededProfileArmyForSide("attacker", seeded, signature);
      } catch (error) {
        // The simulation run path will surface a concrete error if the linked capture cannot load.
      }
    };
    void applyCapture();
    return () => {
      cancelled = true;
    };
  }, [
    applySeededProfileArmyForSide,
    attackerCommanderProfile,
    excludesFromTroopLimit,
    levelOptionsByTroop,
    tierOptionsByTroop,
    troopDefinitions,
    troopGidLookup,
    simulationConfig.combatProfiles,
  ]);

  useEffect(() => {
    const profileId = String(defenderCommanderProfile?._id || "");
    const baselineId = String(defenderCommanderProfile?.linkedReportBaselineId || "").trim();
    if (isCapturedCurrentProfile(defenderCommanderProfile)) return;
    if (!profileId || !baselineId) return;
    const signature = `capture:${profileId}:${baselineId}`;
    if (profilePresetAppliedRef.current.defender === signature) return;
    let cancelled = false;
    const applyCapture = async () => {
      try {
        const baseline = await getReportBaseline(baselineId, { scope: "nation" });
        if (cancelled) return;
        const payload = getAction20019PayloadFromBaseline(baseline);
        const troop = getCaptureTroopForProfile(payload, defenderCommanderProfile, "defender");
        const seeded = seedArmyFromCaptureTroop(troopDefinitions, troopGidLookup, troop);
        if (!seeded.matchedRowCount) {
          setServerParityError("The selected defender report baseline was loaded, but no captured defender troop rows could be mapped.");
          return;
        }
        applySeededProfileArmyForSide("defender", seeded, signature);
      } catch (error) {
        // The simulation run path will surface a concrete error if the linked capture cannot load.
      }
    };
    void applyCapture();
    return () => {
      cancelled = true;
    };
  }, [
    applySeededProfileArmyForSide,
    defenderCommanderProfile,
    excludesFromTroopLimit,
    levelOptionsByTroop,
    tierOptionsByTroop,
    troopDefinitions,
    troopGidLookup,
    simulationConfig.combatProfiles,
  ]);

  useEffect(() => {
    if (!isServerTracePlaying) {
      if (tracePlaybackIntervalRef.current) {
        clearInterval(tracePlaybackIntervalRef.current);
        tracePlaybackIntervalRef.current = null;
      }
      return;
    }

    if (!Array.isArray(serverTraceFrames) || !serverTraceFrames.length) {
      setIsServerTracePlaying(false);
      return;
    }

    const playbackSpeed = Math.max(0.1, Number(tracePlaybackSpeed || 1));
    const baseFps = Math.max(1, Number(serverTraceFps || DEFAULT_TRACE_FPS));
    const baseFrameMs = 1000 / baseFps;
    const intervalMs = Math.max(5, Math.round(baseFrameMs / playbackSpeed));

    tracePlaybackIntervalRef.current = setInterval(() => {
      setServerTraceFrameIndex((prev) => {
        const maxIndex = Math.max(0, serverTraceFrames.length - 1);
        if (prev >= maxIndex) {
          setIsServerTracePlaying(false);
          return maxIndex;
        }
        return prev + 1;
      });
    }, intervalMs);

    return () => {
      if (tracePlaybackIntervalRef.current) {
        clearInterval(tracePlaybackIntervalRef.current);
        tracePlaybackIntervalRef.current = null;
      }
    };
  }, [isServerTracePlaying, serverTraceFrames, tracePlaybackSpeed, serverTraceFps]);

  const editableTroopDefinitions = useMemo(
    () => troopDefinitions,
    [troopDefinitions],
  );

  const attackerSelectedTierMetaByTroop = useMemo(
    () => {
      const out = {};
      editableTroopDefinitions.forEach((troop) => {
        if (Number(attackerArmy?.[troop.id] || 0) <= 0) return;
        const stats = getTierStats(
          troop.id,
          attackerTierByTroop?.[troop.id],
          attackerLevelByTroop?.[troop.id],
          simulationConfig.combatProfiles || {},
          troop.row,
        );
        out[troop.id] = {
          troopGid: Number(stats?.troopGid || troop?.defaultTroopGid || 0) || null,
          specialSkill: stats?.specialSkill || null,
        };
      });
      return out;
    },
    [attackerArmy, attackerTierByTroop, attackerLevelByTroop, editableTroopDefinitions, simulationConfig.combatProfiles],
  );

  const defenderSelectedTierMetaByTroop = useMemo(
    () => {
      const out = {};
      editableTroopDefinitions.forEach((troop) => {
        if (Number(defenderArmy?.[troop.id] || 0) <= 0) return;
        const stats = getTierStats(
          troop.id,
          defenderTierByTroop?.[troop.id],
          defenderLevelByTroop?.[troop.id],
          simulationConfig.combatProfiles || {},
          troop.row,
        );
        out[troop.id] = {
          troopGid: Number(stats?.troopGid || troop?.defaultTroopGid || 0) || null,
          specialSkill: stats?.specialSkill || null,
        };
      });
      return out;
    },
    [defenderArmy, defenderTierByTroop, defenderLevelByTroop, editableTroopDefinitions, simulationConfig.combatProfiles],
  );

  const applyZombieLimit = (side, rawValue) => {
    const zombieLimit = parseArmyValue(rawValue);
    markCustomArmySourceForSide(side);
    if (side === "attacker") {
      setAttackerLimits((prev) => ({ ...prev, zombieLimit }));
      setAttackerArmy((prev) => ({ ...prev, zombie: zombieLimit }));
      return;
    }
    setDefenderLimits((prev) => ({ ...prev, zombieLimit }));
    setDefenderArmy((prev) => ({ ...prev, zombie: zombieLimit }));
  };

  const applyTroopLimit = (side, rawValue) => {
    const requested = parseArmyValue(rawValue);
    markCustomArmySourceForSide(side);
    if (side === "attacker") {
      const currentTotal = sumFleetUnitsExcluding(
        attackerArmy,
        excludesFromTroopLimit,
        attackerTierByTroop,
        attackerLevelByTroop,
        troopDefinitions,
        simulationConfig.combatProfiles || {},
      );
      setAttackerLimits((prev) => ({ ...prev, troopLimit: Math.max(requested, currentTotal) }));
      return;
    }
    const currentTotal = sumFleetUnitsExcluding(
      defenderArmy,
      excludesFromTroopLimit,
      defenderTierByTroop,
      defenderLevelByTroop,
      troopDefinitions,
      simulationConfig.combatProfiles || {},
    );
    setDefenderLimits((prev) => ({ ...prev, troopLimit: Math.max(requested, currentTotal) }));
  };

  const updateArmy = (side, troopId, rawValue) => {
    const nextValue = parseArmyValue(rawValue);
    markCustomArmySourceForSide(side);

    if (side === "attacker") {
      setAttackerArmy((prev) => {
        const currentValue = Number(prev[troopId] || 0);
        const unitSize = getTroopUnitSize(
          troopId,
          attackerTierByTroop,
          attackerLevelByTroop,
          troopDefinitions,
          simulationConfig.combatProfiles || {},
        );
        const currentTotalUnits = sumFleetUnitsExcluding(
          prev,
          excludesFromTroopLimit,
          attackerTierByTroop,
          attackerLevelByTroop,
          troopDefinitions,
          simulationConfig.combatProfiles || {},
        );
        const remainingUnitsForTroop = Math.max(
          0,
          Number(attackerLimits.troopLimit || 0) - (currentTotalUnits - (currentValue * unitSize)),
        );
        const maxTroopsForUnitBudget = Math.floor(remainingUnitsForTroop / unitSize);
        const bypassTroopLimit = isExcludedFromTroopLimit(troopId, excludesFromTroopLimit);
        const constrained = troopId === "zombie"
          ? Number(attackerLimits.zombieLimit || 0)
          : (bypassTroopLimit ? nextValue : Math.min(nextValue, maxTroopsForUnitBudget));
        return { ...prev, [troopId]: constrained };
      });
      return;
    }

    setDefenderArmy((prev) => {
      const currentValue = Number(prev[troopId] || 0);
      const unitSize = getTroopUnitSize(
        troopId,
        defenderTierByTroop,
        defenderLevelByTroop,
        troopDefinitions,
        simulationConfig.combatProfiles || {},
      );
      const currentTotalUnits = sumFleetUnitsExcluding(
        prev,
        excludesFromTroopLimit,
        defenderTierByTroop,
        defenderLevelByTroop,
        troopDefinitions,
        simulationConfig.combatProfiles || {},
      );
      const remainingUnitsForTroop = Math.max(
        0,
        Number(defenderLimits.troopLimit || 0) - (currentTotalUnits - (currentValue * unitSize)),
      );
      const maxTroopsForUnitBudget = Math.floor(remainingUnitsForTroop / unitSize);
      const bypassTroopLimit = isExcludedFromTroopLimit(troopId, excludesFromTroopLimit);
      const constrained = troopId === "zombie"
        ? Number(defenderLimits.zombieLimit || 0)
        : (bypassTroopLimit ? nextValue : Math.min(nextValue, maxTroopsForUnitBudget));
      return { ...prev, [troopId]: constrained };
    });
  };

  const updateTier = (side, troopId, tierValue) => {
    const raw = String(tierValue ?? "");
    const maybeNumber = Number(raw);
    const tier = Number.isFinite(maybeNumber) && raw !== "" ? maybeNumber : raw;
    markCustomArmySourceForSide(side);
    if (side === "attacker") {
      setAttackerTierByTroop((prev) => {
        const next = { ...prev, [troopId]: tier };
        const usedUnits = sumFleetUnitsExcluding(
          attackerArmy,
          excludesFromTroopLimit,
          next,
          attackerLevelByTroop,
          troopDefinitions,
          simulationConfig.combatProfiles || {},
        );
        setAttackerLimits((curr) => ({ ...curr, troopLimit: Math.max(Number(curr.troopLimit || 0), usedUnits) }));
        return next;
      });
      return;
    }
    setDefenderTierByTroop((prev) => {
      const next = { ...prev, [troopId]: tier };
      const usedUnits = sumFleetUnitsExcluding(
        defenderArmy,
        excludesFromTroopLimit,
        next,
        defenderLevelByTroop,
        troopDefinitions,
        simulationConfig.combatProfiles || {},
      );
      setDefenderLimits((curr) => ({ ...curr, troopLimit: Math.max(Number(curr.troopLimit || 0), usedUnits) }));
      return next;
    });
  };

  const updateLevel = (side, troopId, levelValue) => {
    const level = Number(levelValue || 0);
    const nextLevel = Number.isFinite(level) ? level : "";
    markCustomArmySourceForSide(side);
    if (side === "attacker") {
      setAttackerLevelByTroop((prev) => {
        const next = { ...prev, [troopId]: nextLevel };
        const usedUnits = sumFleetUnitsExcluding(
          attackerArmy,
          excludesFromTroopLimit,
          attackerTierByTroop,
          next,
          troopDefinitions,
          simulationConfig.combatProfiles || {},
        );
        setAttackerLimits((curr) => ({ ...curr, troopLimit: Math.max(Number(curr.troopLimit || 0), usedUnits) }));
        return next;
      });
      return;
    }
    setDefenderLevelByTroop((prev) => {
      const next = { ...prev, [troopId]: nextLevel };
      const usedUnits = sumFleetUnitsExcluding(
        defenderArmy,
        excludesFromTroopLimit,
        defenderTierByTroop,
        next,
        troopDefinitions,
        simulationConfig.combatProfiles || {},
      );
      setDefenderLimits((curr) => ({ ...curr, troopLimit: Math.max(Number(curr.troopLimit || 0), usedUnits) }));
      return next;
    });
  };

  const resetArmy = (side) => {
    const empty = createArmyState(troopDefinitions);
    markCustomArmySourceForSide(side);
    if (side === "attacker") {
      setAttackerArmy(empty);
      setAttackerLimits({ zombieLimit: 0, troopLimit: 0 });
      return;
    }
    setDefenderArmy(empty);
    setDefenderLimits({ zombieLimit: 0, troopLimit: 0 });
  };

  const applyPreset = (side, presetKey = "midRange") => {
    const scopedPreset = simulationConfig?.presets?.[presetKey]?.[side]
      || simulationConfig?.presets?.basic?.[side]
      || simulationConfig?.presets?.[side]
      || null;
    if (!scopedPreset || typeof scopedPreset !== "object") return;
    const seeded = createArmyState(troopDefinitions, scopedPreset);
    markCustomArmySourceForSide(side);
    if (side === "attacker") {
      const nextLimits = buildLimitsFromArmy(
        seeded,
        excludesFromTroopLimit,
        attackerTierByTroop,
        attackerLevelByTroop,
        troopDefinitions,
        simulationConfig.combatProfiles || {},
      );
      setAttackerArmy({ ...seeded, zombie: nextLimits.zombieLimit });
      setAttackerLimits(nextLimits);
      return;
    }
    const nextLimits = buildLimitsFromArmy(
      seeded,
      excludesFromTroopLimit,
      defenderTierByTroop,
      defenderLevelByTroop,
      troopDefinitions,
      simulationConfig.combatProfiles || {},
    );
    setDefenderArmy({ ...seeded, zombie: nextLimits.zombieLimit });
    setDefenderLimits(nextLimits);
  };

  const openCommanderPicker = (side) => {
    setCommanderProfileQuery("");
    setCommanderPickerState({ open: true, side });
    if (!isAuthInitializing && isUserAuthenticated && !isCommanderProfilesLoading) {
      void loadCommanderProfiles();
    }
  };

  const closeCommanderPicker = () => {
    setCommanderPickerState((prev) => ({ ...prev, open: false }));
    setCommanderProfileQuery("");
  };

  const selectCommanderProfileForSide = (profile) => {
    const profileId = String(profile?._id || "");
    if (!profileId) return;
    if (commanderPickerState.side === "defender") {
      profilePresetAppliedRef.current.defender = "";
      clearArmyForCommanderSelection("defender");
      setDefenderCommanderProfileId(profileId);
    } else {
      profilePresetAppliedRef.current.attacker = "";
      clearArmyForCommanderSelection("attacker");
      setAttackerCommanderProfileId(profileId);
    }
    clearBattleRunState();
    closeCommanderPicker();
  };

  const openCommanderProfileManager = (profileId = "") => {
    const selectedId = String(profileId || "");
    const profile = commanderProfileById.get(selectedId);
    if (isCapturedCurrentProfile(profile)) {
      setServerParityError("Captured current profiles are read-only. Use My Commander or a manual profile to edit commander data.");
      return;
    }
    const query = selectedId ? `?profileId=${encodeURIComponent(selectedId)}` : "";
    navigate(`/battle-simulation/commander-profiles${query}`);
  };

  const handleCreateCommanderProfileFromPicker = async () => {
    setIsCommanderProfileMutating(true);
    try {
      const created = await createCommanderProfile({
        name: `Commander Profile ${new Date().toLocaleDateString()}`,
        parityProfile: DEFAULT_COMMANDER_PARITY_PROFILE,
        powerSummary: DEFAULT_COMMANDER_POWER_SUMMARY,
        sections: {
          officers: { notes: "", status: "draft", data: { rows: [] } },
          titans: { notes: "", status: "draft" },
          warplane: { notes: "", status: "draft" },
          equipment: { notes: "", status: "draft" },
          antiques: { notes: "", status: "draft" },
          island: { notes: "", status: "draft" },
          biochemicalWorkshop: { notes: "", status: "draft" },
          academy: { notes: "", status: "draft" },
          commanderSkills: { notes: "", status: "draft" },
        },
      });
      if (!created?._id) return;
      await loadCommanderProfiles({ preferredId: created._id });
      if (commanderPickerState.side === "defender") {
        profilePresetAppliedRef.current.defender = "";
        clearArmyForCommanderSelection("defender");
        setDefenderCommanderProfileId(String(created._id));
      } else {
        profilePresetAppliedRef.current.attacker = "";
        clearArmyForCommanderSelection("attacker");
        setAttackerCommanderProfileId(String(created._id));
      }
      closeCommanderPicker();
      openCommanderProfileManager(created._id);
    } catch (error) {
      setServerParityError(
        error?.response?.data?.message
        || error?.message
        || "Failed to create commander profile.",
      );
    } finally {
      setIsCommanderProfileMutating(false);
    }
  };

  const handleResetBattle = () => {
    setIsServerTracePlaying(false);
    setServerTraceFrameIndex(0);
    setServerTraceFrames([]);
    setServerTraceMeta(null);
    setServerBattleReport(null);
    setServerCompareDelta(null);
    setServerReportDiagnostics(null);
    setServerTraceEventsByFrame({});
    setServerWinner(null);
    setServerParityError("");
    setServerParitySummary("");
  };

  const handleSelectReportBaseline = async (baseline) => {
    const baselineId = String(baseline?._id || "");
    if (!baselineId) return;
    setSelectedReportBaselineId(baselineId);
    setSelectedReportBaseline(baseline || null);
    handleResetBattle();
    try {
      const detailed = await getReportBaseline(baselineId, { scope: "nation" });
      if (detailed?._id && String(detailed._id) === baselineId) {
        setSelectedReportBaseline(detailed);
      }
    } catch (error) {
      setReportBaselineError(
        error?.response?.data?.message
        || error?.message
        || "Failed to load report details.",
      );
    }
  };

  const buildParityStacksForSide = (sideArmy, sideTierByTroop, sideLevelByTroop) => {
    const stacks = [];
    const missingTroops = [];
    troopDefinitions.forEach((troop) => {
      const count = Math.max(0, Math.floor(Number(sideArmy?.[troop.id] || 0)));
      if (count <= 0) return;
      const stats = getTierStats(
        troop.id,
        sideTierByTroop?.[troop.id],
        sideLevelByTroop?.[troop.id],
        simulationConfig.combatProfiles || {},
        troop.row,
      );
      const troopGid = Number(stats?.troopGid || troop?.defaultTroopGid || 0);
      if (!Number.isFinite(troopGid) || troopGid <= 0) {
        missingTroops.push(String(troop?.label || troop?.id || "unknown"));
        return;
      }
      stacks.push({
        troopGid,
        count,
      });
    });
    return { stacks, missingTroops };
  };

  const groupTraceEventsByFrame = (events = []) => (
    (Array.isArray(events) ? events : []).reduce((acc, event) => {
      const key = String(Math.max(0, Math.floor(Number(event?.frame || 0))));
      if (!Array.isArray(acc[key])) acc[key] = [];
      acc[key].push(event);
      return acc;
    }, {})
  );

  const buildTraceEventMergeKey = useCallback((event = {}) => ([
    event?.frame ?? "",
    event?.phase ?? "",
    event?.type ?? "",
    event?.side ?? "",
    event?.fromSide ?? event?.sourceSide ?? "",
    event?.toSide ?? event?.targetSide ?? "",
    event?.stackId ?? "",
    event?.fromStackId ?? event?.sourceStackId ?? "",
    event?.toStackId ?? event?.targetStackId ?? event?.targetId ?? "",
    event?.troopId ?? "",
    event?.fromTroopId ?? event?.sourceTroopId ?? "",
    event?.toTroopId ?? event?.targetTroopId ?? "",
    event?.skillGid ?? "",
    event?.spellGid ?? "",
    event?.sourceId ?? "",
    event?.sourceGid ?? "",
    event?.damage ?? "",
    event?.deadUnits ?? "",
    event?.deadTroops ?? "",
    event?.miss ? "miss" : "",
    event?.banSkill ? "ban" : "",
    event?.misfire ? "misfire" : "",
  ]).map((value) => String(value)).join("|"), []);

  const createTraceVisualContext = () => ({
    lastOfficerSkillBySide: new Map(),
    lastPoseByStackId: new Map(),
    lastPoseByTroopKey: new Map(),
    lastSourceByTargetStackId: new Map(),
  });

  const resolveTraceFrameEvents = useCallback((frame) => {
    const frameRows = Array.isArray(frame?.events) ? frame.events : [];
    const timelineRows = serverTraceEventsByFrame?.[String(Number(frame?.frame || 0))] || [];
    if (!frameRows.length) return timelineRows;
    if (!timelineRows.length) return frameRows;
    const merged = [];
    const seen = new Set();
    [...frameRows, ...timelineRows].forEach((event) => {
      const key = buildTraceEventMergeKey(event);
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(event);
    });
    return merged;
  }, [serverTraceEventsByFrame, buildTraceEventMergeKey]);

  const projectTracePlaybackFrameAtIndex = useCallback((frameIndex, options = {}) => {
    const normalizedIndex = Math.max(
      0,
      Math.min(
        Math.floor(Number(frameIndex || 0)),
        Math.max(0, (Array.isArray(serverTraceFrames) ? serverTraceFrames.length : 0) - 1),
      ),
    );
    const frame = Array.isArray(serverTraceFrames) ? serverTraceFrames[normalizedIndex] : null;
    if (!frame) return null;
    const frameEvents = resolveTraceFrameEvents(frame);
    return projectFrame({
      frame,
      frameEvents,
      troopIds: [],
      mode: traceProjectionMode,
      battleType: selectedBattleType,
      traceMeta: serverTraceMeta,
      visualContext: options?.visualContext || null,
    });
  }, [serverTraceFrames, traceProjectionMode, selectedBattleType, serverTraceMeta, resolveTraceFrameEvents]);

  const applyServerParityRunResult = ({
    result = {},
    compareDelta = null,
    compareMode = false,
    compareVariantSource = null,
    reportBaseline = null,
    fallbackBattleType = selectedBattleType,
    label = "Server parity completed",
  } = {}) => {
    const traceFrames = Array.isArray(result?.frameTrace?.frames) ? result.frameTrace.frames : [];
    const traceMeta = result?.frameTrace?.meta && typeof result.frameTrace.meta === "object"
      ? result.frameTrace.meta
      : null;
    const traceFps = Math.max(1, Number(result?.frameTrace?.fps || DEFAULT_TRACE_FPS));
    const timelineEvents = Array.isArray(result?.timeline) ? result.timeline : [];
    const eventsByFrame = groupTraceEventsByFrame(timelineEvents);
    setServerTraceFrames(traceFrames);
    setServerTraceMeta(traceMeta);
    setServerBattleReport(result?.battleReport && typeof result.battleReport === "object" ? result.battleReport : null);
    setServerCompareDelta(compareDelta);
    setServerTraceFps(traceFps);
    setServerTraceEventsByFrame(eventsByFrame);
    setServerTraceFrameIndex(0);
    setIsServerTracePlaying(false);

    setServerWinner(result?.winner || null);
    const resolvedBattleType = result?.battleType || result?.modifiers?.battleType || fallbackBattleType;
    if (BATTLE_TYPE_OPTIONS.some((option) => option.value === resolvedBattleType)) {
      setSelectedBattleType(resolvedBattleType);
    }
    const resolvedEngine = result?.engineVersion || PARITY_ENGINE_VERSION;
    const runtimeSource = String(result?.runtimeSource || "unknown");
    const reportBaselineId = String(
      compareVariantSource?.reportBaselineId
      || reportBaseline?._id
      || "",
    ).trim();
    const reportIdentityKey = String(
      compareVariantSource?.reportIdentityKey
      || reportBaseline?.identityKey
      || "",
    ).trim();
    const derivedReportId = String(
      result?.battleId
      || reportBaseline?.reportId
      || parseReportIdFromIdentityKey(reportIdentityKey)
      || "",
    ).trim();
    setServerReportDiagnostics({
      runtimeSource,
      reportBaselineId: reportBaselineId || "",
      reportId: derivedReportId || "",
    });
    const luaRunnerFallback = String(result?.luaBridgeMeta?.runnerFallback || "").trim();
    const luaRunnerFallbackReason = String(result?.luaBridgeMeta?.runnerFallbackReason || "").trim();
    const attackerDelta = Number(
      result?.battleReportComparison?.sideMetrics?.attacker?.totals?.absoluteLostUnitsDelta || 0,
    );
    const defenderDelta = Number(
      result?.battleReportComparison?.sideMetrics?.defender?.totals?.absoluteLostUnitsDelta || 0,
    );
    const hasComparison = Boolean(result?.battleReportComparison?.available);
    const luaFallbackSummary = luaRunnerFallback
      ? ` - lua-runner-fallback: ${luaRunnerFallback}${luaRunnerFallbackReason ? ` (${luaRunnerFallbackReason})` : ""}`
      : "";
    const stoppedEarly = Boolean(result?.stoppedEarly);
    const stopReason = String(result?.stopReason || "").trim();
    const statusLabel = stoppedEarly ? "Simulation stopped early" : label;
    setServerParitySummary(
      `${statusLabel} (${resolvedEngine} / ${result.mode || "unknown"} / ${resolvedBattleType}${compareMode ? " / report-baseline-compare" : ""}) - runtime: ${runtimeSource}${luaFallbackSummary}${stoppedEarly ? ` - stop reason: ${stopReason || "unknown"}` : ""} - winner: ${result.winner || "n/a"} - frames: ${Number(result.framesSimulated || 0)} - trace frames: ${traceFrames.length}${hasComparison ? ` - delta(lost): atk ${Math.round(attackerDelta).toLocaleString()} / def ${Math.round(defenderDelta).toLocaleString()}` : ""}${compareDelta ? ` - compare winner changed: ${compareDelta.winnerChanged ? "yes" : "no"}` : ""}`,
    );
  };

  const handleRunServerParity = async () => {
    if (isServerSimulating) return;
    if (isBattleConfigLoading) {
      setServerParityError("Battle config is still loading. Please try again in a moment.");
      return;
    }
    if (isAuthInitializing || !isUserAuthenticated) {
      setServerParityError("Authentication is still initializing. Please wait and try again.");
      return;
    }
    if (!isBattleConfigLoaded) {
      const configReady = await loadSimulationConfig();
      if (!configReady) {
        setServerParityError("Failed to load battle config. Please refresh or sign in again.");
        return;
      }
    }
    if (!Array.isArray(commanderProfiles) || commanderProfiles.length === 0) {
      await loadCommanderProfiles();
    }
    if (!attackerCommanderProfile || !defenderCommanderProfile) {
      setServerParityError("Attacker and defender commander profiles are required.");
      return;
    }
    const attackerArmySourceStatus = validateArmySourceForSide("attacker", attackerCommanderProfile);
    if (!attackerArmySourceStatus.ok) {
      setServerParityError(attackerArmySourceStatus.message);
      return;
    }
    const defenderArmySourceStatus = validateArmySourceForSide("defender", defenderCommanderProfile);
    if (!defenderArmySourceStatus.ok) {
      setServerParityError(defenderArmySourceStatus.message);
      return;
    }
    setIsServerSimulating(true);
    setIsServerTracePlaying(false);
    setServerTraceFrameIndex(0);
    setServerTraceFrames([]);
    setServerTraceMeta(null);
    setServerBattleReport(null);
    setServerCompareDelta(null);
    setServerReportDiagnostics(null);
    setServerTraceFps(DEFAULT_TRACE_FPS);
    setTraceProjectionMode(DEFAULT_BATTLE_PROJECTION_MODE);
    setServerTraceEventsByFrame({});
    setServerParityError("");
    setServerParitySummary("");
    setServerWinner(null);
    try {
      const apiUrl = API_URL;
      const executeOptions = {
        battleType: selectedBattleType,
        maxFrames: SERVER_PARITY_MAX_TRACE_FRAMES,
        maxRuntimeMs: SERVER_PARITY_MAX_RUNTIME_MS,
        includeTimeline: true,
        timelineLimit: Math.max(20000, SERVER_PARITY_MAX_TRACE_FRAMES * 6),
        engineVersion: PARITY_ENGINE_VERSION,
        includeFrameSnapshots: true,
        frameSnapshotLimit: SERVER_PARITY_MAX_TRACE_FRAMES,
      };
      const attackerReportBaselineId = String(attackerCommanderProfile?.linkedReportBaselineId || "").trim();
      const defenderReportBaselineId = String(defenderCommanderProfile?.linkedReportBaselineId || "").trim();
      if (
        attackerReportBaselineId
        && defenderReportBaselineId
        && !isCapturedCurrentProfile(attackerCommanderProfile)
        && !isCapturedCurrentProfile(defenderCommanderProfile)
      ) {
        const attackerBaseline = await getReportBaseline(attackerReportBaselineId, { scope: "nation" });
        const defenderBaseline = attackerReportBaselineId === defenderReportBaselineId
          ? attackerBaseline
          : await getReportBaseline(defenderReportBaselineId, { scope: "nation" });
        const linkedCaptureBattleInput = buildLinkedCapturePairBattleInput({
          attackerBaseline,
          defenderBaseline,
          attackerProfile: attackerCommanderProfile,
          defenderProfile: defenderCommanderProfile,
          selectedBattleType,
        });
        if (!linkedCaptureBattleInput) {
          setServerParityError("Selected commander profiles are linked to reports, but the captured replay rows could not be composed.");
          return;
        }
        const linkedBattleType = String(linkedCaptureBattleInput?.battleType || "").trim()
          || (Number(linkedCaptureBattleInput?.bt) === 1 ? "uranium_mine" : selectedBattleType);
        const response = await axiosInstance.post(`${apiUrl}/battle-simulation/parity/execute`, {
          battleInput: linkedCaptureBattleInput,
          options: {
            ...executeOptions,
            battleType: linkedBattleType,
          },
        });
        applyServerParityRunResult({
          result: response?.data || {},
          reportBaseline: attackerBaseline,
          fallbackBattleType: linkedBattleType,
          label: "Captured commander pairing simulation completed",
        });
        return;
      }

      const attackerBuild = buildParityStacksForSide(
        attackerArmy,
        attackerTierByTroop,
        attackerLevelByTroop,
      );
      const defenderBuild = buildParityStacksForSide(
        defenderArmy,
        defenderTierByTroop,
        defenderLevelByTroop,
      );
      const missingLabels = [...attackerBuild.missingTroops, ...defenderBuild.missingTroops];
      if (missingLabels.length) {
        setServerParityError(`Missing extracted troop GID for: ${missingLabels.join(", ")}`);
        return;
      }
      const attackerStacks = attackerBuild.stacks;
      const defenderStacks = defenderBuild.stacks;
      const attackerCommanderParity = buildParityCommanderFromProfile(attackerCommanderProfile);
      const defenderCommanderParity = buildParityCommanderFromProfile(defenderCommanderProfile);

      const battleInput = {
        id: Date.now(),
        battleType: selectedBattleType,
        bt: selectedBattleType === "uranium_mine" ? 1 : 0,
        a_aid: 1,
        d_aid: 2,
        att: 1001,
        def: 2002,
        p_r: 200,
        a_pr: 1000,
        d_pr: 1000,
        r_s: 5000,
        attackerStacks,
        defenderStacks,
        commanderProfiles: {
          attacker: attackerCommanderParity,
          defender: defenderCommanderParity,
        },
      };
      const canRunReportBaselineCompare = (
        Boolean(attackerReportBaselineId)
        && attackerReportBaselineId === defenderReportBaselineId
        && !isCapturedCurrentProfile(attackerCommanderProfile)
        && !isCapturedCurrentProfile(defenderCommanderProfile)
        && String(attackerCommanderProfile?._id || "").trim()
        && String(defenderCommanderProfile?._id || "").trim()
      );

      let result = {};
      let compareDelta = null;
      let compareMode = false;
      let compareVariantSource = null;
      if (canRunReportBaselineCompare) {
        compareMode = true;
        // Strict report compare must replay baseline battle input; variant applies profile overrides server-side.
        const compareOptions = { ...executeOptions };
        delete compareOptions.battleType;
        const comparePayload = {
          baseline: {
            mode: "report_baseline",
            reportBaselineId: attackerReportBaselineId,
            attackerProfileId: String(attackerCommanderProfile?._id || "").trim(),
            defenderProfileId: String(defenderCommanderProfile?._id || "").trim(),
            useProfileTroops: false,
          },
          variant: {
            mode: "report_plus_profile_overrides",
            reportBaselineId: attackerReportBaselineId,
            attackerProfileId: String(attackerCommanderProfile?._id || "").trim(),
            defenderProfileId: String(defenderCommanderProfile?._id || "").trim(),
            useProfileTroops: false,
          },
          options: compareOptions,
        };
        const compareResponse = await axiosInstance.post(`${apiUrl}/battle-simulation/parity/compare`, comparePayload);
        compareDelta = compareResponse?.data?.delta && typeof compareResponse.data.delta === "object"
          ? compareResponse.data.delta
          : null;
        compareVariantSource = compareResponse?.data?.variant?.source && typeof compareResponse.data.variant.source === "object"
          ? compareResponse.data.variant.source
          : null;
        result = compareResponse?.data?.variant?.run || {};
      } else {
        const payload = {
          battleInput,
          options: executeOptions,
        };
        const response = await axiosInstance.post(`${apiUrl}/battle-simulation/parity/execute`, payload);
        result = response?.data || {};
      }

      applyServerParityRunResult({
        result,
        compareDelta,
        compareMode,
        compareVariantSource,
        fallbackBattleType: selectedBattleType,
        label: "Server parity completed",
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Server parity execution failed.";
      setServerParityError(message);
    } finally {
      setIsServerSimulating(false);
    }
  };

  const handleRunSelectedReportParity = async () => {
    if (isServerSimulating) return;
    if (isAuthInitializing || !isUserAuthenticated) {
      setServerParityError("Authentication is still initializing. Please wait and try again.");
      return;
    }
    const baseline = selectedReportBaseline && typeof selectedReportBaseline === "object"
      ? selectedReportBaseline
      : null;
    const battleInput = baseline?.battleInput && typeof baseline.battleInput === "object"
      ? baseline.battleInput
      : null;
    if (!baseline || !battleInput) {
      setServerParityError("Select an existing report with captured replay input first.");
      return;
    }
    setIsServerSimulating(true);
    setIsServerTracePlaying(false);
    setServerTraceFrameIndex(0);
    setServerTraceFrames([]);
    setServerTraceMeta(null);
    setServerBattleReport(null);
    setServerCompareDelta(null);
    setServerReportDiagnostics(null);
    setServerTraceFps(DEFAULT_TRACE_FPS);
    setTraceProjectionMode(DEFAULT_BATTLE_PROJECTION_MODE);
    setServerTraceEventsByFrame({});
    setServerParityError("");
    setServerParitySummary("");
    setServerWinner(null);
    try {
      const apiUrl = API_URL;
      const fallbackBattleType = String(battleInput?.battleType || "").trim()
        || (Number(battleInput?.bt) === 1 ? "uranium_mine" : "nation_siege");
      const executeOptions = {
        battleType: fallbackBattleType,
        maxFrames: SERVER_PARITY_MAX_TRACE_FRAMES,
        maxRuntimeMs: SERVER_PARITY_MAX_RUNTIME_MS,
        includeTimeline: true,
        timelineLimit: Math.max(20000, SERVER_PARITY_MAX_TRACE_FRAMES * 6),
        engineVersion: PARITY_ENGINE_VERSION,
        includeFrameSnapshots: true,
        frameSnapshotLimit: SERVER_PARITY_MAX_TRACE_FRAMES,
      };
      const response = await axiosInstance.post(`${apiUrl}/battle-simulation/parity/execute`, {
        battleInput,
        options: {
          ...executeOptions,
          captureTruth: baseline?.captureTruth && typeof baseline.captureTruth === "object"
            ? baseline.captureTruth
            : null,
          preferCaptureTruthBattleReport: true,
        },
      });
      applyServerParityRunResult({
        result: response?.data || {},
        reportBaseline: baseline,
        fallbackBattleType,
        label: "Captured report simulation completed",
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Captured report simulation failed.";
      setServerParityError(message);
    } finally {
      setIsServerSimulating(false);
    }
  };

  const toggleServerTracePlayback = () => {
    if (!Array.isArray(serverTraceFrames) || !serverTraceFrames.length) return;
    setIsServerTracePlaying((prev) => {
      const maxIndex = Math.max(0, serverTraceFrames.length - 1);
      if (!prev && serverTraceFrameIndex >= maxIndex) {
        setServerTraceFrameIndex(0);
      }
      return !prev;
    });
  };

  const stepServerTracePlayback = () => {
    if (!Array.isArray(serverTraceFrames) || !serverTraceFrames.length) return;
    setIsServerTracePlaying(false);
    setServerTraceFrameIndex((prev) => Math.min(prev + 1, Math.max(0, serverTraceFrames.length - 1)));
  };

  const stepServerTracePlaybackBackward = () => {
    if (!Array.isArray(serverTraceFrames) || !serverTraceFrames.length) return;
    setIsServerTracePlaying(false);
    setServerTraceFrameIndex((prev) => Math.max(0, prev - 1));
  };

  const seekServerTraceFrame = (nextIndex) => {
    if (!Array.isArray(serverTraceFrames) || !serverTraceFrames.length) return;
    const maxIndex = Math.max(0, serverTraceFrames.length - 1);
    setIsServerTracePlaying(false);
    setServerTraceFrameIndex(Math.min(maxIndex, Math.max(0, Math.floor(Number(nextIndex || 0)))));
  };

  const hasServerTrace = Array.isArray(serverTraceFrames) && serverTraceFrames.length > 0;
  const clampedServerTraceFrameIndex = hasServerTrace
    ? Math.min(Math.max(0, serverTraceFrameIndex), serverTraceFrames.length - 1)
    : 0;
  const activeServerTraceFrame = hasServerTrace
    ? serverTraceFrames[clampedServerTraceFrameIndex]
    : null;
  const traceLinePersistenceFrames = Math.max(
    1,
    Math.round(Number(serverTraceFps || DEFAULT_TRACE_FPS) * TRACE_LINE_PERSIST_SECONDS),
  );
  const activeTracePlayback = useMemo(() => {
    if (!hasServerTrace) return null;
    const visualContext = createTraceVisualContext();
    const startFrameIndex = Math.max(0, clampedServerTraceFrameIndex - (traceLinePersistenceFrames - 1));
    let projection = null;
    for (let frameIndex = startFrameIndex; frameIndex <= clampedServerTraceFrameIndex; frameIndex += 1) {
      projection = projectTracePlaybackFrameAtIndex(frameIndex, { visualContext });
    }
    return projection;
  }, [hasServerTrace, clampedServerTraceFrameIndex, traceLinePersistenceFrames, projectTracePlaybackFrameAtIndex]);
  const persistentLineOverlay = useMemo(() => {
    if (!hasServerTrace || !activeTracePlayback) return null;
    const startFrameIndex = Math.max(0, clampedServerTraceFrameIndex - (traceLinePersistenceFrames - 1));
    const visualContext = createTraceVisualContext();
    const attackEvents = [];
    const targetLines = [];
    const movementVectors = [];
    for (let frameIndex = startFrameIndex; frameIndex <= clampedServerTraceFrameIndex; frameIndex += 1) {
      const projection = projectTracePlaybackFrameAtIndex(frameIndex, { visualContext });
      if (!projection) continue;
      const ageFrames = clampedServerTraceFrameIndex - frameIndex;
      const ageRatio = traceLinePersistenceFrames <= 1
        ? 0
        : (ageFrames / (traceLinePersistenceFrames - 1));
      const fadeOpacity = Math.max(TRACE_LINE_MIN_OPACITY, 1 - ageRatio);
      (Array.isArray(projection?.attackEvents) ? projection.attackEvents : []).forEach((event, eventIndex) => {
        attackEvents.push({
          ...event,
          persistedFromFrame: frameIndex,
          persistedAgeFrames: ageFrames,
          persistedIndex: eventIndex,
          opacity: Math.max(TRACE_LINE_MIN_OPACITY, fadeOpacity * 0.92),
        });
      });
      (Array.isArray(projection?.targetLines) ? projection.targetLines : []).forEach((line, lineIndex) => {
        targetLines.push({
          ...line,
          persistedFromFrame: frameIndex,
          persistedAgeFrames: ageFrames,
          persistedIndex: lineIndex,
          opacity: Math.max(TRACE_LINE_MIN_OPACITY, fadeOpacity * 0.8),
        });
      });
      (Array.isArray(projection?.movementVectors) ? projection.movementVectors : []).forEach((line, lineIndex) => {
        movementVectors.push({
          ...line,
          persistedFromFrame: frameIndex,
          persistedAgeFrames: ageFrames,
          persistedIndex: lineIndex,
          opacity: Math.max(TRACE_LINE_MIN_OPACITY, fadeOpacity * 0.72),
        });
      });
    }
    return { attackEvents, targetLines, movementVectors };
  }, [
    hasServerTrace,
    activeTracePlayback,
    clampedServerTraceFrameIndex,
    traceLinePersistenceFrames,
    projectTracePlaybackFrameAtIndex,
  ]);

  const displayedAttackerArmy = activeTracePlayback
    ? activeTracePlayback.attackerFrameArmy
    : attackerArmy;
  const displayedDefenderArmy = activeTracePlayback
    ? activeTracePlayback.defenderFrameArmy
    : defenderArmy;
  const displayedAttackerTiers = attackerTierByTroop;
  const displayedDefenderTiers = defenderTierByTroop;
  const displayedAttackerLevels = attackerLevelByTroop;
  const displayedDefenderLevels = defenderLevelByTroop;
  const displayedAttackerStackHp = activeTracePlayback
    ? activeTracePlayback.attackerFrameHp
    : {};
  const displayedDefenderStackHp = activeTracePlayback
    ? activeTracePlayback.defenderFrameHp
    : {};
  const displayedAttackEvents = hasServerTrace
    ? (persistentLineOverlay?.attackEvents || activeTracePlayback?.attackEvents || [])
    : (activeTracePlayback?.attackEvents || []);
  const displayedTargetLines = hasServerTrace
    ? (persistentLineOverlay?.targetLines || activeTracePlayback?.targetLines || [])
    : [];
  const displayedMovementVectors = hasServerTrace
    ? (persistentLineOverlay?.movementVectors || activeTracePlayback?.movementVectors || [])
    : [];
  const displayedCastMarkers = hasServerTrace ? (activeTracePlayback?.castMarkers || []) : [];
  const displayedAreaEffects = hasServerTrace ? (activeTracePlayback?.areaEffects || []) : [];
  const displayedStackStates = hasServerTrace
    ? (activeTracePlayback?.stackStatesByStackId || activeTracePlayback?.stackStatesByTroop || {})
    : {};
  const displayedTraceRenderStacks = hasServerTrace ? (activeTracePlayback?.renderStacks || []) : [];
  const displayedDebugEvents = hasServerTrace
    ? (activeTracePlayback?.debugEvents || [])
    : [];
  const displayedFrameActionLog = hasServerTrace
    ? (activeTracePlayback?.frameActionLog || [])
    : [];
  const configSkillCatalogById = useMemo(() => {
    const map = {};
    const officerSkillOptionsById = new Map(
      [...OFFICER_ACTIVE_SKILL_OPTIONS, ...OFFICER_PASSIVE_SKILL_OPTIONS]
        .map((entry) => [String(Math.trunc(Number(entry?.skillId))), entry])
        .filter(([id]) => Number.isFinite(Number(id)) && Number(id) > 0),
    );
    const catalog = Array.isArray(simulationConfig?.troopCatalog?.skillCatalog)
      ? simulationConfig.troopCatalog.skillCatalog
      : [];
    catalog.forEach((entry) => {
      const id = Math.trunc(Number(entry?.skillId));
      if (!Number.isFinite(id) || id <= 0) return;
      map[String(id)] = {
        label: String(entry?.label || `Skill ${id}`),
        description: String(entry?.description || ""),
      };
    });
    [...OFFICER_ACTIVE_SKILL_OPTIONS, ...OFFICER_PASSIVE_SKILL_OPTIONS].forEach((entry) => {
      const id = Math.trunc(Number(entry?.skillId));
      if (!Number.isFinite(id) || id <= 0) return;
      map[String(id)] = {
        label: String(entry?.label || `Skill ${id}`),
        description: String(entry?.description || ""),
      };
    });
    OFFICER_BASE_ROWS.forEach((officer) => {
      const officerId = Math.trunc(Number(officer?.officerId));
      if (!Number.isFinite(officerId) || officerId <= 0) return;
      const officerLabel = String(officer?.label || `Officer ${officerId}`);
      const officerIconPath = getOfficerDisplayIconPath(officerId, true)
        || getOfficerDisplayIconPath(officerId, false)
        || officer?.skinIconPath
        || officer?.iconPath
        || "";
      [officer?.baseSkillId, officer?.breakthroughSkillId].forEach((rawSkillId) => {
        const id = Math.trunc(Number(rawSkillId));
        if (!Number.isFinite(id) || id <= 0) return;
        const skillOption = officerSkillOptionsById.get(String(id)) || {};
        const existing = map[String(id)] || {};
        map[String(id)] = {
          ...existing,
          label: String(existing.label || skillOption?.label || officer?.baseSkillLabel || officer?.breakthroughSkillLabel || `Skill ${id}`),
          description: String(existing.description || skillOption?.description || ""),
          sourceKind: "officer",
          officerId,
          officerLabel,
          officerIconPath,
        };
        (Array.isArray(skillOption?.effectIds) ? skillOption.effectIds : []).forEach((rawEffectId) => {
          const effectId = Math.trunc(Number(rawEffectId));
          if (!Number.isFinite(effectId) || effectId <= 0) return;
          const effectExisting = map[String(effectId)] || {};
          map[String(effectId)] = {
            ...effectExisting,
            label: String(effectExisting.label || skillOption?.label || map[String(id)]?.label || `Skill ${id}`),
            description: String(effectExisting.description || skillOption?.description || ""),
            sourceKind: "officer",
            officerId,
            officerLabel,
            officerIconPath,
            parentSkillId: id,
          };
        });
      });
    });
    return map;
  }, [simulationConfig?.troopCatalog?.skillCatalog]);
  const runtimeSkillCatalogById = useMemo(() => {
    const map = {};
    const catalog = serverTraceMeta?.runtimeSpellCatalog;
    if (!catalog || typeof catalog !== "object") return map;
    Object.entries(catalog).forEach(([gidKey, value]) => {
      const gid = Math.trunc(Number(gidKey));
      if (!Number.isFinite(gid) || gid <= 0) return;
      map[String(gid)] = {
        label: String(value?.label || value?.code || `Spell ${gid}`),
        description: String(value?.code || ""),
      };
    });
    return map;
  }, [serverTraceMeta?.runtimeSpellCatalog]);
  const combinedSkillCatalogById = useMemo(
    () => ({ ...runtimeSkillCatalogById, ...configSkillCatalogById }),
    [runtimeSkillCatalogById, configSkillCatalogById],
  );
  const displayedBattleTick = hasServerTrace
    ? Number(activeServerTraceFrame?.frame || 0)
    : 0;
  const displayedBattleClockSeconds = hasServerTrace
    ? Number(activeServerTraceFrame?.clockSeconds || 0)
    : 0;

  const buildStatCardMap = (army, tiers, levels, stackHpMap = {}) => {
    const map = {};
    troopDefinitions.forEach((troop) => {
      const count = Number(army?.[troop.id] || 0);
      if (count <= 0) return;
      const stats = getTierStats(troop.id, tiers?.[troop.id], levels?.[troop.id], simulationConfig.combatProfiles || {}, troop.row);
      const maxHpPool = Math.max(1, Math.round(Number(stats.hp || 0) * count));
      const currentHpPool = Number(stackHpMap?.[troop.id] || maxHpPool);
      map[troop.id] = {
        tier: tiers?.[troop.id],
        level: levels?.[troop.id],
        count,
        hpRatio: Math.max(0, Math.min(1, currentHpPool / maxHpPool)),
        perUnit: {
          attack: Number(stats.attack || 0),
          hp: Number(stats.hp || 0),
          defense: Number(stats.defense || 0),
          range: Number(stats.attackRange || 0),
          cooldown: Number(stats.attackCooldown || 0),
        },
        total: {
          attack: Math.round(Number(stats.attack || 0) * count),
          hp: Math.round(Number(stats.hp || 0) * count),
          defense: Math.round(Number(stats.defense || 0) * count),
        },
      };
    });
    return map;
  };

  const attackerStatCardMap = buildStatCardMap(displayedAttackerArmy, displayedAttackerTiers, displayedAttackerLevels, displayedAttackerStackHp);
  const defenderStatCardMap = buildStatCardMap(displayedDefenderArmy, displayedDefenderTiers, displayedDefenderLevels, displayedDefenderStackHp);

  const attackerFleetUnitsUsed = sumFleetUnitsExcluding(
    attackerArmy,
    excludesFromTroopLimit,
    attackerTierByTroop,
    attackerLevelByTroop,
    troopDefinitions,
    simulationConfig.combatProfiles || {},
  );
  const defenderFleetUnitsUsed = sumFleetUnitsExcluding(
    defenderArmy,
    excludesFromTroopLimit,
    defenderTierByTroop,
    defenderLevelByTroop,
    troopDefinitions,
    simulationConfig.combatProfiles || {},
  );
  const attackerProfilePreview = useMemo(() => buildPendingReportForSide({
    sideArmy: attackerArmy,
    sideTierByTroop: attackerTierByTroop,
    sideLevelByTroop: attackerLevelByTroop,
    troopDefinitions,
    combatProfiles: simulationConfig.combatProfiles || {},
    troopDisplayByGid,
    profile: attackerCommanderProfile,
  }), [
    attackerArmy,
    attackerTierByTroop,
    attackerLevelByTroop,
    troopDefinitions,
    simulationConfig.combatProfiles,
    troopDisplayByGid,
    attackerCommanderProfile,
  ]);
  const defenderProfilePreview = useMemo(() => buildPendingReportForSide({
    sideArmy: defenderArmy,
    sideTierByTroop: defenderTierByTroop,
    sideLevelByTroop: defenderLevelByTroop,
    troopDefinitions,
    combatProfiles: simulationConfig.combatProfiles || {},
    troopDisplayByGid,
    profile: defenderCommanderProfile,
  }), [
    defenderArmy,
    defenderTierByTroop,
    defenderLevelByTroop,
    troopDefinitions,
    simulationConfig.combatProfiles,
    troopDisplayByGid,
    defenderCommanderProfile,
  ]);
  const displayedBattleReport = useMemo(() => {
    if (!serverBattleReport || typeof serverBattleReport !== "object") return serverBattleReport;
    const attackerBonuses = Array.isArray(serverBattleReport.attackerBonuses) && serverBattleReport.attackerBonuses.length
      ? serverBattleReport.attackerBonuses
      : attackerProfilePreview.bonuses;
    const defenderBonuses = Array.isArray(serverBattleReport.defenderBonuses) && serverBattleReport.defenderBonuses.length
      ? serverBattleReport.defenderBonuses
      : defenderProfilePreview.bonuses;
    return {
      ...serverBattleReport,
      attackerBonuses,
      defenderBonuses,
      attackerSourceAttribution: serverBattleReport.attackerSourceAttribution || attackerProfilePreview.sourceAttribution,
      defenderSourceAttribution: serverBattleReport.defenderSourceAttribution || defenderProfilePreview.sourceAttribution,
    };
  }, [serverBattleReport, attackerProfilePreview, defenderProfilePreview]);
  const activeCommanderPickerProfile = commanderPickerState.side === "defender"
    ? defenderCommanderProfile
    : attackerCommanderProfile;
  const filteredCommanderProfiles = !String(commanderProfileQuery || "").trim()
    ? commanderProfiles
    : commanderProfiles.filter((profile) => {
      const query = String(commanderProfileQuery || "").trim().toLowerCase();
      return [
        profile?.name,
        profile?.linkedCommanderName,
        profile?.commanderName,
        profile?.allianceShort,
        profile?.nationNumber,
        profile?.gameCommanderId,
        getCommanderProfileSourceLabel(profile),
      ].some((value) => String(value || "").toLowerCase().includes(query));
    });
  const filteredReportBaselines = !String(reportBaselineQuery || "").trim()
    ? reportBaselines
    : reportBaselines.filter((baseline) => {
      const query = String(reportBaselineQuery || "").trim().toLowerCase();
      const attacker = buildBaselineCommanderProfile(baseline, "attacker");
      const defender = buildBaselineCommanderProfile(baseline, "defender");
      return [
        baseline?.label,
        baseline?.reportId,
        baseline?.identityKey,
        baseline?.nationNumber,
        baseline?.source,
        attacker?.name,
        attacker?.allianceShort,
        defender?.name,
        defender?.allianceShort,
      ].some((value) => String(value || "").toLowerCase().includes(query));
    });
  const selectedCapturedReport = buildCapturedReportView(selectedReportBaseline);
  const selectedReportAttackerProfile = buildBaselineCommanderProfile(selectedReportBaseline, "attacker");
  const selectedReportDefenderProfile = buildBaselineCommanderProfile(selectedReportBaseline, "defender");
  const selectedReportPayload = selectedReportBaseline?.reportPayload && typeof selectedReportBaseline.reportPayload === "object"
    ? selectedReportBaseline.reportPayload
    : {};
  const selectedReportParticipantCount = Array.isArray(selectedReportPayload?.marchingTroops)
    ? selectedReportPayload.marchingTroops.length
    : 0;
  const selectedReportBattleType = String(selectedReportBaseline?.battleInput?.battleType || "").trim()
    || (Number(selectedReportBaseline?.battleInput?.bt) === 1 ? "uranium_mine" : "nation_siege");

  return (
    <PageShell maxWidth={false} className="battle-simulation-page">
      <div className="battle-simulation-tabs" role="tablist" aria-label="Battle Simulation Sections">
        <button
          type="button"
          className={activeBattleSimulationTab === "existing_reports" ? "active" : ""}
          onClick={() => setActiveBattleSimulationTab("existing_reports")}
        >
          Existing Reports
        </button>
        <button
          type="button"
          className={activeBattleSimulationTab === "simulate_battle" ? "active" : ""}
          onClick={() => setActiveBattleSimulationTab("simulate_battle")}
        >
          Simulate Battle
        </button>
      </div>
      {activeBattleSimulationTab === "existing_reports" ? (
        <div className="battle-report-browser">
          <SectionCard className="battle-report-browser-list">
            <div className="battle-report-browser-header">
              <div>
                <h3>Existing Reports</h3>
                <p>{isReportBaselinesLoading ? "Loading captured reports..." : `${filteredReportBaselines.length.toLocaleString()} reports`}</p>
              </div>
              <button type="button" onClick={() => loadReportBaselines({ preferredId: selectedReportBaselineId })} disabled={isReportBaselinesLoading}>
                Refresh
              </button>
            </div>
            <input
              className="battle-report-search"
              type="search"
              value={reportBaselineQuery}
              onChange={(event) => setReportBaselineQuery(event.target.value)}
              placeholder="Search commander, alliance, report id"
            />
            {reportBaselineError ? (
              <div className="battle-server-error">{reportBaselineError}</div>
            ) : null}
            <div className="battle-report-baseline-list">
              {filteredReportBaselines.map((baseline) => {
                const attacker = buildBaselineCommanderProfile(baseline, "attacker");
                const defender = buildBaselineCommanderProfile(baseline, "defender");
                const isSelected = String(baseline?._id || "") === String(selectedReportBaselineId || "");
                const truth = buildCapturedReportView(baseline);
                const totalUnits = toNonNegativeInt(truth?.attackerSummary?.totalUnits, 0)
                  + toNonNegativeInt(truth?.defenderSummary?.totalUnits, 0);
                return (
                  <button
                    type="button"
                    key={String(baseline?._id || baseline?.identityKey || baseline?.reportId)}
                    className={`battle-report-baseline-row${isSelected ? " selected" : ""}`}
                    onClick={() => handleSelectReportBaseline(baseline)}
                  >
                    <span className="battle-report-baseline-title">
                      {attacker.name} vs {defender.name}
                    </span>
                    <span className="battle-report-baseline-meta">
                      {formatCompactDateTime(baseline?.capturedAtUtc)} | {baseline?.reportId || "no report id"} | {baseline?.nationNumber || "nation unknown"}
                    </span>
                    <span className="battle-report-baseline-meta">
                      {attacker.allianceShort || "?"} -> {defender.allianceShort || "?"} | {formatUnits(totalUnits)} units
                    </span>
                  </button>
                );
              })}
              {!filteredReportBaselines.length && !isReportBaselinesLoading ? (
                <div className="battle-report-browser-empty">No captured battle reports are available for this nation.</div>
              ) : null}
            </div>
          </SectionCard>

          <div className="battle-report-browser-detail">
            <SectionCard className="battle-report-detail-card">
              <div className="battle-report-detail-header">
                <div>
                  <h3>{selectedReportBaseline ? `${selectedReportAttackerProfile.name} vs ${selectedReportDefenderProfile.name}` : "Select a Report"}</h3>
                  <p>
                    {selectedReportBaseline
                      ? `${formatCompactDateTime(selectedReportBaseline?.capturedAtUtc)} | ${selectedReportBattleType.replace(/_/g, " ")} | ${selectedReportParticipantCount} participants`
                      : "Choose a captured report to inspect the payload and replay data."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRunSelectedReportParity}
                  disabled={isServerSimulating || !selectedReportBaseline}
                >
                  {isServerSimulating ? "Simulating..." : "Simulate Report"}
                </button>
              </div>
              {selectedReportBaseline ? (
                <div className="battle-report-metadata-grid">
                  <div><span>Report ID</span><strong>{selectedReportBaseline.reportId || "n/a"}</strong></div>
                  <div><span>Action</span><strong>{selectedReportBaseline.action || "n/a"}</strong></div>
                  <div><span>Source</span><strong>{selectedReportBaseline.source || "n/a"}</strong></div>
                  <div><span>Identity Key</span><strong>{selectedReportBaseline.identityKey || "n/a"}</strong></div>
                </div>
              ) : null}
              <BattleReportSection
                battleReport={selectedCapturedReport}
                attackerProfile={selectedReportAttackerProfile}
                defenderProfile={selectedReportDefenderProfile}
                title="Captured Report Data"
                emptyMessage="Select a captured report to view troop results."
                showBonuses={false}
              />
            </SectionCard>

            <SectionCard className="battlefield-panel">
              <div className="battlefield-panel-header">
                <h3>Replay Simulation</h3>
                <p>
                  Time: {formatBattleClock(displayedBattleClockSeconds)} | Tick: {displayedBattleTick}
                  {serverWinner ? ` | Winner: ${serverWinner}` : ""}
                </p>
                <button type="button" className="battle-debug-toggle" onClick={() => setShowDebugOverlay((prev) => !prev)}>
                  {showDebugOverlay ? "Hide Debug" : "Show Debug"}
                </button>
              </div>
              <BattlefieldFormation
                config={simulationConfig}
                attackEvents={displayedAttackEvents}
                attackerStatCardMap={attackerStatCardMap}
                defenderStatCardMap={defenderStatCardMap}
                showHoverStats={true}
                showDebugOverlay={showDebugOverlay}
                stackStates={displayedStackStates}
                debugEvents={displayedDebugEvents}
                targetLines={displayedTargetLines}
                movementVectors={displayedMovementVectors}
                castMarkers={displayedCastMarkers}
                areaEffects={displayedAreaEffects}
                traceRenderStacks={displayedTraceRenderStacks}
                frameActionLog={displayedFrameActionLog}
                troopDisplayByGid={troopDisplayByGid}
                skillCatalogById={combinedSkillCatalogById}
                onToggleTracePlayback={toggleServerTracePlayback}
                onToggleFrameActionPanel={() => setShowFrameActionPanel((prev) => !prev)}
                onStepTracePlayback={stepServerTracePlayback}
                onStepTracePlaybackBackward={stepServerTracePlaybackBackward}
                onSeekTracePlayback={seekServerTraceFrame}
                onChangeTracePlaybackSpeed={(value) => setTracePlaybackSpeed(value)}
                onChangeProjectionMode={(value) => setTraceProjectionMode(value)}
                onReset={handleResetBattle}
                onRunServerParity={handleRunSelectedReportParity}
                isServerSimulating={isServerSimulating}
                isTracePlaying={isServerTracePlaying}
                tracePlaybackFrameIndex={clampedServerTraceFrameIndex}
                tracePlaybackFrameCount={serverTraceFrames.length}
                tracePlaybackSpeed={tracePlaybackSpeed}
                traceProjectionMode={traceProjectionMode}
                tracePlaybackBaseFps={serverTraceFps}
                showFrameActionPanel={showFrameActionPanel}
                traceMeta={serverTraceMeta}
                serverParitySummary={serverParitySummary}
                serverParityError={serverParityError}
                runServerParityLabel="Simulate Report"
                runningServerParityLabel="Simulating Report..."
              />
              <BattleReportSection
                battleReport={displayedBattleReport}
                compareDelta={serverCompareDelta}
                attackerProfile={selectedReportAttackerProfile}
                defenderProfile={selectedReportDefenderProfile}
                reportDiagnostics={serverReportDiagnostics}
                skillCatalogById={combinedSkillCatalogById}
                title={displayedBattleReport?.source === "score_map_truth_projection" ? "Exact Report Result" : "Simulated Replay Result"}
                emptyMessage="Simulate the selected report to generate a replay trace and battle report."
              />
            </SectionCard>
          </div>
        </div>
      ) : (
      <div className="battle-simulation-layout">
        <ArmySetupPanel
          title="Attacker"
          troopDefinitions={editableTroopDefinitions}
          values={attackerArmy}
          limits={attackerLimits}
          currentTroopTotal={attackerFleetUnitsUsed}
          rowOrder={simulationConfig.rowOrder}
          rowLabels={simulationConfig.rowLabels}
          tierByTroop={attackerTierByTroop}
          tierOptionsByTroop={tierOptionsByTroop}
          tierOptionLabelsByTroop={tierOptionLabelsByTroop}
          levelByTroop={attackerLevelByTroop}
          levelOptionsByTroop={levelOptionsByTroop}
          levelOptionLabelsByTroop={levelOptionLabelsByTroop}
          selectedTierMetaByTroop={attackerSelectedTierMetaByTroop}
          onTierChange={(troopId, tier) => updateTier("attacker", troopId, tier)}
          onLevelChange={(troopId, level) => updateLevel("attacker", troopId, level)}
          onChange={(troopId, value) => updateArmy("attacker", troopId, value)}
          onZombieLimitChange={(value) => applyZombieLimit("attacker", value)}
          onTroopLimitChange={(value) => applyTroopLimit("attacker", value)}
          onReset={() => resetArmy("attacker")}
          onApplyPreset={(presetKey) => applyPreset("attacker", presetKey)}
          commanderCard={(
            <CommanderCard
              title="Attacker Commander"
              profile={attackerCommanderProfile}
              onChangeCommander={() => openCommanderPicker("attacker")}
              onEditProfile={() => openCommanderProfileManager(attackerCommanderProfile?._id)}
              disabled={isCommanderProfilesLoading || isCommanderProfileMutating}
            />
          )}
        />

        <SectionCard className="battlefield-panel">
          <div className="battlefield-panel-header">
            <h3>Battlefield Formation</h3>
            <p>
              Time: {formatBattleClock(displayedBattleClockSeconds)} | Tick: {displayedBattleTick}
              {serverWinner ? ` | Winner: ${serverWinner}` : ""}
            </p>
            <div className="battle-type-selector">
              <label htmlFor="battle-type-select">Battle Type</label>
              <select
                id="battle-type-select"
                className="battle-type-select"
                value={selectedBattleType}
                onChange={(event) => setSelectedBattleType(event.target.value)}
                disabled={isServerSimulating}
              >
                {BATTLE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className="battle-debug-toggle" onClick={() => setShowDebugOverlay((prev) => !prev)}>
              {showDebugOverlay ? "Hide Debug" : "Show Debug"}
            </button>
          </div>
          <BattlefieldFormation
            config={simulationConfig}
            attackEvents={displayedAttackEvents}
            attackerStatCardMap={attackerStatCardMap}
            defenderStatCardMap={defenderStatCardMap}
            showHoverStats={true}
            showDebugOverlay={showDebugOverlay}
            stackStates={displayedStackStates}
            debugEvents={displayedDebugEvents}
            targetLines={displayedTargetLines}
            movementVectors={displayedMovementVectors}
            castMarkers={displayedCastMarkers}
            areaEffects={displayedAreaEffects}
            traceRenderStacks={displayedTraceRenderStacks}
            frameActionLog={displayedFrameActionLog}
            troopDisplayByGid={troopDisplayByGid}
            skillCatalogById={combinedSkillCatalogById}
            onToggleTracePlayback={toggleServerTracePlayback}
            onToggleFrameActionPanel={() => setShowFrameActionPanel((prev) => !prev)}
            onStepTracePlayback={stepServerTracePlayback}
            onStepTracePlaybackBackward={stepServerTracePlaybackBackward}
            onSeekTracePlayback={seekServerTraceFrame}
            onChangeTracePlaybackSpeed={(value) => setTracePlaybackSpeed(value)}
            onChangeProjectionMode={(value) => setTraceProjectionMode(value)}
            onReset={handleResetBattle}
            onRunServerParity={handleRunServerParity}
            isServerSimulating={isServerSimulating}
            isTracePlaying={isServerTracePlaying}
            tracePlaybackFrameIndex={clampedServerTraceFrameIndex}
            tracePlaybackFrameCount={serverTraceFrames.length}
            tracePlaybackSpeed={tracePlaybackSpeed}
            traceProjectionMode={traceProjectionMode}
            tracePlaybackBaseFps={serverTraceFps}
            showFrameActionPanel={showFrameActionPanel}
            traceMeta={serverTraceMeta}
            serverParitySummary={serverParitySummary}
            serverParityError={serverParityError}
          />
          <BattleReportSection
            battleReport={displayedBattleReport}
            compareDelta={serverCompareDelta}
            attackerProfile={attackerCommanderProfile}
            defenderProfile={defenderCommanderProfile}
            reportDiagnostics={serverReportDiagnostics}
            skillCatalogById={combinedSkillCatalogById}
          />
        </SectionCard>

        <ArmySetupPanel
          title="Defender"
          troopDefinitions={editableTroopDefinitions}
          values={defenderArmy}
          limits={defenderLimits}
          currentTroopTotal={defenderFleetUnitsUsed}
          rowOrder={simulationConfig.rowOrder}
          rowLabels={simulationConfig.rowLabels}
          tierByTroop={defenderTierByTroop}
          tierOptionsByTroop={tierOptionsByTroop}
          tierOptionLabelsByTroop={tierOptionLabelsByTroop}
          levelByTroop={defenderLevelByTroop}
          levelOptionsByTroop={levelOptionsByTroop}
          levelOptionLabelsByTroop={levelOptionLabelsByTroop}
          selectedTierMetaByTroop={defenderSelectedTierMetaByTroop}
          onTierChange={(troopId, tier) => updateTier("defender", troopId, tier)}
          onLevelChange={(troopId, level) => updateLevel("defender", troopId, level)}
          onChange={(troopId, value) => updateArmy("defender", troopId, value)}
          onZombieLimitChange={(value) => applyZombieLimit("defender", value)}
          onTroopLimitChange={(value) => applyTroopLimit("defender", value)}
          onReset={() => resetArmy("defender")}
          onApplyPreset={(presetKey) => applyPreset("defender", presetKey)}
          commanderCard={(
            <CommanderCard
              title="Defender Commander"
              profile={defenderCommanderProfile}
              onChangeCommander={() => openCommanderPicker("defender")}
              onEditProfile={() => openCommanderProfileManager(defenderCommanderProfile?._id)}
              disabled={isCommanderProfilesLoading || isCommanderProfileMutating}
            />
          )}
        />
      </div>
      )}
      <CommanderSelectionModal
        isOpen={commanderPickerState.open}
        sideLabel={commanderPickerState.side === "defender" ? "Defender" : "Attacker"}
        currentProfile={activeCommanderPickerProfile}
        profiles={filteredCommanderProfiles}
        isLoading={isCommanderProfilesLoading}
        searchQuery={commanderProfileQuery}
        onSearchChange={setCommanderProfileQuery}
        onSelectProfile={selectCommanderProfileForSide}
        onCreateProfile={handleCreateCommanderProfileFromPicker}
        onOpenProfileManager={() => openCommanderProfileManager(activeCommanderPickerProfile?._id)}
        onClose={closeCommanderPicker}
        isBusy={isCommanderProfileMutating}
      />
    </PageShell>
  );
};

export default BattleSimulation;

